import { createQuickNotes } from './quick-notes';
// SwatNotes web client — Quill editor with attachments + inline images

import Quill from 'quill';
import { installPlainTextCopy } from './plain-text-copy';
import { installViewport } from './viewport';
import { marked } from 'marked';
import type { Tokens } from 'marked';
import DOMPurify from 'dompurify';
import 'quill/dist/quill.snow.css';
import { wireInkModal, openInkModal, isInkModalOpen, dismissInkModal, closeInkModal } from './ink';
import './ink';
import { installPencilHighlighter, setHighlighterTool, getHighlighterTool } from './pencil';
import type { HighlighterTool } from './pencil';
import { installDrawOverlay, activateDrawMode, deactivateDrawMode, finishDrawMode, hasUnsavedInk } from './overlay';
import {
  isOfflineId, persistNotebooks, persistNotes, persistCollections,
  loadCachedNotebooks, loadCachedNotes, loadCachedCollections,
  queueCreate, cancelCreate, queueUpdate, queueDelete,
  hasPendingOps, clearOfflineData, clearAllOfflineData, flushOfflineQueue, clearQueuedUpdate,
  notePendingStatus, setVaultCachePolicy, vaultCacheDisabled, mergePendingNotes, clearNotebookCache,
} from './offline';
import type { PendingUpdate } from './offline';

// Sanitize markdown output:
// - Escape raw HTML blocks so inline <script>/<iframe> from untrusted sources
//   (e.g. load-from-URL) cannot execute on preview.
// - Rewrite javascript:/data: hrefs in links and images to "#" so clicking a
//   [crafted](javascript:evil()) link cannot run code in the page context.
marked.use({
  renderer: {
    html(token: Tokens.HTML | Tokens.Tag): string {
      return token.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },
    link({ href, title, text }: Tokens.Link): string {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer"${title ? ` title="${title}"` : ''}>${text}</a>`;
    },
  },
  walkTokens(token) {
    if (token.type === 'link' || token.type === 'image') {
      const t = token as { href: string };
      if (!/^(https?:|mailto:|#|\/)/i.test(t.href)) {
        t.href = '#';
      }
    }
  },
});

// ----------------- Utilities -----------------

function renderMarkdown(text: string): string {
  return DOMPurify.sanitize(marked.parse(text) as string);
}

function logWarn(context: string, error: unknown): void {
  console.warn(`[${context}]`, error);
}

function extractApiError(j: unknown): string {
  if (j && typeof j === 'object') {
    const err = (j as Record<string, unknown>).error;
    if (err && typeof err === 'object') {
      const msg = (err as Record<string, unknown>).message;
      if (typeof msg === 'string') return msg;
    }
    if (typeof err === 'string') return err;
  }
  return '';
}

// ----------------- Types -----------------
interface Notebook {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
}
interface Note {
  id: string;
  title: string;
  content_json: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  title_modified: boolean;
  collection_id: string | null;
  notebook_id: string | null;
  note_type: string;
}
interface Collection {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  is_locked: boolean;
}
interface Reminder {
  id: string;
  note_id: string;
  trigger_time: string;
  triggered: boolean;
  created_at: string;
  sound_enabled: boolean | null;
  sound_type: string | null;
  shake_enabled: boolean | null;
  glow_enabled: boolean | null;
}
interface ReminderEvent {
  reminder_id: string;
  note_id: string;
  note_title: string;
  trigger_time: string;
}
interface Attachment {
  id: string;
  note_id: string;
  blob_hash: string;
  filename: string;
  mime_type: string;
  size: number;
  created_at: string;
}

// ----------------- API helper -----------------
async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body !== undefined) {
    (opts.headers as Record<string, string>)['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(path, opts);
  if (res.status === 423) {
    if ($('vault-unlock-modal').classList.contains('hidden')) {
      waitForVaultUnlock().then(() => location.reload());
    }
    throw new Error('vault_locked');
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    let incorrectSecret = false;
    try {
      const j = await res.json();
      const extracted = extractApiError(j);
      if (extracted) msg = extracted;
      // A rejected PIN/password is not an expired passkey session. Keep the
      // server's 401 contract, but distinguish its explicit credential error.
      incorrectSecret = (path === '/auth/catlock/unlock' && j?.error === 'incorrect PIN')
        || j?.error?.code === 'INCORRECT_VAULT_PASSWORD';
    } catch { /* Use the HTTP status if the error body is unavailable. */ }
    if (res.status === 401 && !incorrectSecret) {
      showLogin();
      throw new Error('unauthenticated');
    }
    throw Object.assign(new Error(msg), { status: res.status });
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

// ----------------- Formatters -----------------
function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v.toFixed(v >= 100 || u === 0 ? 0 : 1)} ${units[u]}`;
}

function fileIconSvg(): string {
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;
}

function blobUrl(hash: string): string {
  return `/api/attachments/blob/${encodeURIComponent(hash)}`;
}

// Download URL for an attachment: forces a download with the correct filename
// regardless of file type (the server sends Content-Disposition: attachment).
function downloadUrl(hash: string, filename: string): string {
  return `${blobUrl(hash)}?download=1&name=${encodeURIComponent(filename)}`;
}

// ----------------- Quill Delta helpers -----------------
interface QuillDelta { ops?: Array<{ insert?: unknown }> }

function deltaToPlain(json: string): string {
  try {
    const d: QuillDelta = JSON.parse(json);
    if (!d || !Array.isArray(d.ops)) return '';
    return d.ops.map((op) => (typeof op.insert === 'string' ? op.insert : '')).join('');
  } catch { return ''; }
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Ensure the HTML has a <base> tag with target="_blank" (so links open in new
// tab rather than trying to navigate the sandboxed iframe). When href is given
// it is also set so that relative resource URLs (CSS, JS, images) resolve
// correctly against the original page origin.
function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function injectBase(html: string, href?: string): string {
  const safeHref = href ? escapeAttr(href) : undefined;
  if (/<base\b/i.test(html)) {
    return html.replace(/<base\b([^>]*)>/i, (_, attrs) => {
      const withHref = safeHref && !/\bhref\s*=/i.test(attrs) ? ` href="${safeHref}"${attrs}` : attrs;
      const withTarget = /\btarget\s*=/i.test(withHref) ? withHref : `${withHref} target="_blank"`;
      return `<base${withTarget}>`;
    });
  }
  const tag = safeHref ? `<base href="${safeHref}" target="_blank">` : `<base target="_blank">`;
  return /<head\b/i.test(html)
    ? html.replace(/(<head\b[^>]*>)/i, `$1${tag}`)
    : tag + html;
}

function notePreviewText(n: { note_type: string; content_json: string }): string {
  if (n.note_type === 'rich') return deltaToPlain(n.content_json);
  if (n.note_type === 'html') return stripHtml(n.content_json);
  return n.content_json;
}

// ----------------- Quill custom blots: inline attachments -----------------
/* eslint-disable @typescript-eslint/no-explicit-any */
const BlockEmbed = Quill.import('blots/block/embed') as any;

interface AttachmentImageValue {
  attachmentId: string;
  blobHash: string;
  mimeType: string;
  filename: string;
}

class AttachmentImageBlot extends BlockEmbed {
  static blotName = 'attachment-image';
  static tagName = 'div';
  static className = 'ql-attachment-image';

  static create(value: AttachmentImageValue): HTMLElement {
    const node = super.create() as HTMLElement;
    node.setAttribute('data-attachment-id', value.attachmentId || '');
    node.setAttribute('data-blob-hash', value.blobHash);
    node.setAttribute('data-mime-type', value.mimeType || '');
    node.setAttribute('data-filename', value.filename || '');
    node.setAttribute('contenteditable', 'false');
    node.classList.add('attachment-image-wrapper');

    const img = document.createElement('img');
    img.alt = value.filename || 'Attached image';
    img.className = 'attachment-image';
    img.src = blobUrl(value.blobHash);
    img.addEventListener('click', () => {
      window.open(img.src, '_blank');
    });
    node.appendChild(img);
    return node;
  }

  static value(node: HTMLElement): AttachmentImageValue {
    return {
      attachmentId: node.getAttribute('data-attachment-id') || '',
      blobHash: node.getAttribute('data-blob-hash') || '',
      mimeType: node.getAttribute('data-mime-type') || '',
      filename: node.getAttribute('data-filename') || '',
    };
  }
}

interface AttachmentFileValue {
  attachmentId: string;
  blobHash: string;
  mimeType: string;
  filename: string;
  size: number;
}

class AttachmentFileBlot extends BlockEmbed {
  static blotName = 'attachment-file';
  static tagName = 'div';
  static className = 'ql-attachment-file';

  static create(value: AttachmentFileValue): HTMLElement {
    const node = super.create() as HTMLElement;
    node.setAttribute('data-attachment-id', value.attachmentId || '');
    node.setAttribute('data-blob-hash', value.blobHash);
    node.setAttribute('data-mime-type', value.mimeType || '');
    node.setAttribute('data-filename', value.filename || '');
    node.setAttribute('data-size', String(value.size || 0));
    node.setAttribute('contenteditable', 'false');
    node.classList.add('attachment-file-chip');

    const icon = document.createElement('span');
    icon.className = 'att-icon';
    icon.innerHTML = fileIconSvg();

    const info = document.createElement('div');
    info.className = 'att-info';
    const fn = document.createElement('div');
    fn.className = 'att-filename';
    fn.textContent = value.filename || 'file';
    const sz = document.createElement('div');
    sz.className = 'att-size';
    sz.textContent = formatFileSize(value.size || 0);
    info.appendChild(fn);
    info.appendChild(sz);

    const dl = document.createElement('a');
    dl.className = 'att-download';
    dl.href = downloadUrl(value.blobHash, value.filename || 'file');
    dl.download = value.filename || 'file';
    dl.title = `Download ${value.filename || 'file'}`;
    dl.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;

    node.appendChild(icon);
    node.appendChild(info);
    node.appendChild(dl);
    return node;
  }

  static value(node: HTMLElement): AttachmentFileValue {
    return {
      attachmentId: node.getAttribute('data-attachment-id') || '',
      blobHash: node.getAttribute('data-blob-hash') || '',
      mimeType: node.getAttribute('data-mime-type') || '',
      filename: node.getAttribute('data-filename') || '',
      size: parseInt(node.getAttribute('data-size') || '0', 10),
    };
  }
}

Quill.register({ 'formats/attachment-image': AttachmentImageBlot }, true);
Quill.register({ 'formats/attachment-file': AttachmentFileBlot }, true);
/* eslint-enable @typescript-eslint/no-explicit-any */

// ----------------- State -----------------
const state = {
  notebooks: [] as Notebook[],
  currentNotebook: null as Notebook | null,
  notes: [] as Note[],
  collections: [] as Collection[],
  currentFilter: 'all' as string,
  currentId: null as string | null,
  dirty: false,
  saveTimer: null as number | null,
  reminders: [] as Reminder[],
  attachments: [] as Attachment[],
  evtSource: null as EventSource | null,
  editingCatId: null as string | null,
  quill: null as Quill | null,
  suppressQuillChange: false,
  previewMode: false,
  noteType: 'rich' as string,
  iframeBlobUrl: null as string | null,
  noteHistory: [] as string[],
  // Tracks whether the user has typed in the title field during the current
  // editor session. Lets us avoid lying to the backend with title_modified=true
  // on every body-only save.
  titleEditedThisSession: false,
  offlineMode: false,
  globalSearch: false,
  globalSearchResults: null as Note[] | null,
  globalSearchTimer: null as number | null,
  catlockConfigured: false,
  catlockSessionUnlocked: false,
  trashNotes: [] as Note[],
  trashCount: 0,
  trashLoaded: false,
  viewMode: 'cards' as ViewMode,
};

type ViewMode = 'cards' | 'list' | 'compact';
const VIEW_MODE_KEY = 'swatnotes-view-mode';
const VIEW_MODES: readonly ViewMode[] = ['cards', 'list', 'compact'] as const;

function loadViewMode(): ViewMode {
  const saved = localStorage.getItem(VIEW_MODE_KEY);
  return (VIEW_MODES as readonly string[]).includes(saved ?? '') ? (saved as ViewMode) : 'cards';
}
function updateViewToggleUI() {
  document.querySelectorAll<HTMLButtonElement>('#view-toggle .view-toggle-btn').forEach(btn => {
    const active = btn.dataset.view === state.viewMode;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-pressed', String(active));
  });
  const grid = document.getElementById('notes-grid');
  if (grid && state.currentFilter !== 'trash') grid.setAttribute('data-view', state.viewMode);
}
function setViewMode(mode: ViewMode) {
  if (state.viewMode === mode) return;
  state.viewMode = mode;
  localStorage.setItem(VIEW_MODE_KEY, mode);
  updateViewToggleUI();
  renderGrid();
}

// ----------------- UI helpers -----------------
const $ = (id: string) => document.getElementById(id) as HTMLElement;

function showLogin() {
  cancelCatlockUnlock?.();
  document.querySelectorAll('#app .modal').forEach(modal => modal.classList.add('hidden'));
  $('app').classList.add('hidden');
  $('login-screen').classList.remove('hidden');
  ($('passkey-signin') as HTMLButtonElement).focus();
}
function showApp() {
  $('login-screen').classList.add('hidden');
  $('app').classList.remove('hidden');
}

// ─── Vault UI ────────────────────────────────────────────────────────────────

function showVaultUnlock() {
  $('vault-unlock-modal').classList.remove('hidden');
  ($('vault-unlock-password') as HTMLInputElement).value = '';
  ($('vault-unlock-error') as HTMLElement).classList.add('hidden');
  ($('vault-unlock-password') as HTMLInputElement).focus();
}

function hideVaultUnlock() {
  $('vault-unlock-modal').classList.add('hidden');
}

/** Show unlock modal and return a promise that resolves when vault is unlocked. */
function waitForVaultUnlock(): Promise<void> {
  return new Promise((resolve) => {
    showVaultUnlock();
    const btn = $('vault-unlock-submit') as HTMLButtonElement;
    const input = $('vault-unlock-password') as HTMLInputElement;
    const errEl = $('vault-unlock-error') as HTMLElement;

    async function attempt() {
      const password = input.value;
      if (!password) { input.focus(); return; }
      btn.disabled = true;
      btn.textContent = 'Unlocking…';
      errEl.classList.add('hidden');
      try {
        await api('POST', '/auth/vault/unlock', { password });
        hideVaultUnlock();
        resolve();
      } catch (e) {
        errEl.textContent = (e as Error).message || 'Incorrect password';
        errEl.classList.remove('hidden');
        input.value = '';
        input.focus();
      } finally {
        btn.disabled = false;
        btn.textContent = 'Unlock';
      }
    }

    btn.onclick = attempt;
    input.onkeydown = (e) => { if (e.key === 'Enter') attempt(); };
    ($('vault-unlock-signout') as HTMLButtonElement).onclick = () => {
      showLogin();
      hideVaultUnlock();
      logout();
    };
  });
}

/** Show vault setup modal. Resolves when vault is configured or the user skips. */
function offerVaultSetup(): Promise<void> {
  return new Promise((resolve) => {
    $('vault-setup-modal').classList.remove('hidden');
    const btn = $('vault-setup-submit') as HTMLButtonElement;
    const skipBtn = $('vault-setup-skip') as HTMLButtonElement;
    const pwInput = $('vault-setup-password') as HTMLInputElement;
    const confirmInput = $('vault-setup-confirm') as HTMLInputElement;
    const errEl = $('vault-setup-error') as HTMLElement;

    pwInput.value = '';
    confirmInput.value = '';
    errEl.classList.add('hidden');
    pwInput.focus();

    async function attempt() {
      const pw = pwInput.value;
      const confirm = confirmInput.value;
      errEl.classList.add('hidden');
      if (pw.length < 8) {
        errEl.textContent = 'Password must be at least 8 characters.';
        errEl.classList.remove('hidden');
        return;
      }
      if (pw !== confirm) {
        errEl.textContent = 'Passwords do not match.';
        errEl.classList.remove('hidden');
        return;
      }
      btn.disabled = true;
      skipBtn.disabled = true;
      btn.textContent = 'Encrypting…';
      try {
        if (pendingSync) await pendingSync;
        if (state.dirty || pendingSave) await flushSave();
        if (hasPendingOps()) throw new Error('Sync offline changes before enabling encryption.');
        if (quickNotes?.dirty && !await quickNotes.save()) throw new Error('Save Quick Notes before enabling encryption.');
        await api('POST', '/auth/vault/setup', { password: pw });
        await quickNotes.load(sanitizeQuickNotes);
        localStorage.removeItem('vaultSetupDeclined');
        pwInput.value = '';
        confirmInput.value = '';
        $('vault-setup-modal').classList.add('hidden');
        $('vault-lock-btn').classList.remove('hidden');
        setVaultCachePolicy(true);
        setOfflineMode(false);
        document.body.classList.remove('offline-mode');
        clearAllOfflineData();
        toast('Vault enabled — notes are now encrypted at rest');
        resolve();
      } catch (e) {
        errEl.textContent = (e as Error).message || 'Setup failed';
        errEl.classList.remove('hidden');
      } finally {
        btn.disabled = false;
        skipBtn.disabled = false;
        btn.textContent = 'Encrypt my notes';
      }
    }

    btn.onclick = attempt;
    pwInput.onkeydown = (e) => { if (e.key === 'Enter') confirmInput.focus(); };
    confirmInput.onkeydown = (e) => { if (e.key === 'Enter') attempt(); };
    skipBtn.onclick = () => {
      localStorage.setItem('vaultSetupDeclined', '1');
      $('vault-setup-modal').classList.add('hidden');
      resolve();
    };
  });
}

// ─── Category lock (catlock) ─────────────────────────────────────────────────

async function checkCatlockStatus() {
  try {
    const cs = await api<{ setup: boolean; unlocked: boolean }>('GET', '/auth/catlock/status');
    state.catlockConfigured = cs.setup;
    state.catlockSessionUnlocked = cs.unlocked;
    $('catlock-lock-btn').classList.toggle('hidden', !(cs.setup && cs.unlocked));
  } catch { /* non-critical */ }
}

function openCatlockSetup(): Promise<void> {
  return new Promise((resolve) => {
    const modal = $('catlock-setup-modal');
    modal.classList.remove('hidden');
    const pinInput = $('catlock-setup-pin') as HTMLInputElement;
    const confirmInput = $('catlock-setup-confirm') as HTMLInputElement;
    const errEl = $('catlock-setup-error') as HTMLElement;
    const submitBtn = $('catlock-setup-submit') as HTMLButtonElement;
    const cancelBtn = $('catlock-setup-cancel') as HTMLButtonElement;

    pinInput.value = '';
    confirmInput.value = '';
    errEl.classList.add('hidden');
    pinInput.focus();

    async function attempt() {
      const pin = pinInput.value;
      const confirm = confirmInput.value;
      errEl.classList.add('hidden');
      if (pin.length < 4) {
        errEl.textContent = 'PIN must be at least 4 characters.';
        errEl.classList.remove('hidden');
        return;
      }
      if (pin !== confirm) {
        errEl.textContent = 'PINs do not match.';
        errEl.classList.remove('hidden');
        return;
      }
      submitBtn.disabled = true;
      submitBtn.textContent = 'Saving…';
      try {
        await api('POST', '/auth/catlock/setup', { pin });
        state.catlockConfigured = true;
        state.catlockSessionUnlocked = true;
        $('catlock-lock-btn').classList.remove('hidden');
        modal.classList.add('hidden');
        toast('Category lock PIN set');
        resolve();
      } catch (e) {
        errEl.textContent = (e as Error).message || 'Setup failed';
        errEl.classList.remove('hidden');
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Set PIN';
      }
    }

    submitBtn.onclick = attempt;
    pinInput.onkeydown = (e) => { if (e.key === 'Enter') confirmInput.focus(); };
    confirmInput.onkeydown = (e) => { if (e.key === 'Enter') attempt(); };
    cancelBtn.onclick = () => { modal.classList.add('hidden'); resolve(); };
  });
}

let catlockUnlockPromise: Promise<boolean> | null = null;
let cancelCatlockUnlock: (() => void) | null = null;

function waitForCatlockUnlock(): Promise<boolean> {
  if (catlockUnlockPromise) return catlockUnlockPromise;
  const pending = new Promise<boolean>((resolve) => {
    const modal = $('catlock-unlock-modal');
    modal.classList.remove('hidden');
    const pinInput = $('catlock-unlock-pin') as HTMLInputElement;
    const errEl = $('catlock-unlock-error');
    const submitBtn = $('catlock-unlock-submit') as HTMLButtonElement;
    const cancelBtn = $('catlock-unlock-cancel') as HTMLButtonElement;
    let finished = false;
    const finish = (unlocked: boolean) => {
      if (finished) return;
      finished = true;
      modal.classList.add('hidden');
      pinInput.value = '';
      resolve(unlocked);
    };
    cancelCatlockUnlock = () => finish(false);
    pinInput.value = '';
    submitBtn.disabled = false;
    submitBtn.textContent = 'Unlock';
    errEl.classList.add('hidden');
    pinInput.focus();

    async function attempt() {
      if (finished || submitBtn.disabled) return;
      const pin = pinInput.value;
      if (!pin) { pinInput.focus(); return; }
      submitBtn.disabled = true;
      submitBtn.textContent = 'Unlocking…';
      errEl.classList.add('hidden');
      try {
        await api('POST', '/auth/catlock/unlock', { pin });
        if (finished) return;
        state.catlockSessionUnlocked = true;
        $('catlock-lock-btn').classList.remove('hidden');
        finish(true);
      } catch (e) {
        if (finished) return;
        errEl.textContent = (e as Error).message || 'Could not unlock categories. Try again.';
        errEl.classList.remove('hidden');
        pinInput.focus();
        pinInput.select();
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Unlock';
      }
    }

    submitBtn.onclick = attempt;
    pinInput.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); void attempt(); } };
    cancelBtn.onclick = () => finish(false);
  });
  catlockUnlockPromise = pending.finally(() => {
    catlockUnlockPromise = null;
    cancelCatlockUnlock = null;
  });
  return catlockUnlockPromise;
}

// ─── Settings modal ──────────────────────────────────────────────────────────

async function openSettings() {
  const modal = $('settings-modal');
  modal.classList.remove('hidden');
  const vaultStatusEl = $('settings-vault-status');
  const catlockStatusEl = $('settings-catlock-status');

  // Vault section
  try {
    const vs = await api<{ setup: boolean; unlocked: boolean }>('GET', '/auth/vault/status');
    if (vs.setup) {
      vaultStatusEl.innerHTML = `<div class="settings-vault-row">
        <p>🔒 Vault active. All notes, trash, Quick Notes and attachments are checked on every unlock.</p>
        <label>Current password<input id="settings-vault-password" type="password" autocomplete="current-password" /></label>
        <button id="settings-vault-disable" class="danger">Disable encryption</button>
        <p id="settings-vault-error" role="alert"></p>
      </div>`;
      $('settings-vault-disable').addEventListener('click', async () => {
        const passwordInput = $('settings-vault-password') as HTMLInputElement;
        if (!passwordInput.value) { $('settings-vault-error').textContent = 'Enter your current vault password.'; return; }
        if (!await confirmDialog({ title: 'Decrypt all notes?', message: 'All notes, trash, Quick Notes and attachments will be stored unencrypted. Existing backups keep their original protection.', confirmLabel: 'Decrypt all notes' })) return;
        const button = $('settings-vault-disable') as HTMLButtonElement;
        button.disabled = true;
        button.textContent = 'Decrypting…';
        try {
          if (!await quickNotes.save()) throw new Error('Save Quick Notes before disabling encryption.');
          await api('POST', '/auth/vault/disable', { password: passwordInput.value });
          passwordInput.value = '';
          setVaultCachePolicy(false);
          localStorage.setItem('vaultSetupDeclined', '1');
          $('vault-lock-btn').classList.add('hidden');
          await quickNotes.load(sanitizeQuickNotes);
          if (state.currentNotebook) await loadAll();
          await openSettings();
          toast('Encryption disabled — all current notes and attachments decrypted');
        } catch (error) {
          $('settings-vault-error').textContent = (error as Error).message;
          button.disabled = false;
          button.textContent = 'Disable encryption';
        }
      });
    } else {
      vaultStatusEl.innerHTML = `<div class="settings-vault-row">
        <p>Vault not enabled. Notes are stored unencrypted.</p>
        <button id="settings-vault-enable" class="primary">Enable vault</button>
      </div>`;
      $('settings-vault-enable').addEventListener('click', async () => {
        modal.classList.add('hidden');
        await offerVaultSetup();
      });
    }
  } catch {
    vaultStatusEl.innerHTML = `<p class="hint">Could not load vault status.</p>`;
  }

  const categoryAction = (action: () => Promise<void>) => async () => {
    try { await action(); }
    catch (error) { if ((error as Error).message !== 'unauthenticated') toast((error as Error).message, true); }
  };
  // Catlock section
  try {
    const cs = await api<{ setup: boolean; unlocked: boolean }>('GET', '/auth/catlock/status');
    state.catlockConfigured = cs.setup;
    state.catlockSessionUnlocked = cs.unlocked;
    if (cs.setup) {
      const lockBtnHtml = cs.unlocked
        ? `<button id="settings-catlock-lock" class="ghost">Lock now</button>`
        : `<button id="settings-catlock-unlock" class="ghost">Unlock</button>`;
      catlockStatusEl.innerHTML = `<div class="settings-vault-row">
        <p><strong>${cs.unlocked ? 'Categories unlocked' : 'Categories locked'}</strong><br>${cs.unlocked ? 'Protected categories are visible in this session.' : 'Enter your PIN to show protected categories. Your other notes stay available.'}</p>
        <div class="settings-actions">
          ${lockBtnHtml}
          <button id="settings-catlock-remove" class="danger">Remove PIN</button>
        </div>
      </div>`;
      if (cs.unlocked) {
        $('settings-catlock-lock').addEventListener('click', categoryAction(async () => {
          await api('POST', '/auth/catlock/lock', {});
          state.catlockSessionUnlocked = false;
          $('catlock-lock-btn').classList.add('hidden');
          modal.classList.add('hidden');
          if (state.currentNotebook) await loadAll();
          toast('Categories locked');
        }));
      } else {
        $('settings-catlock-unlock').addEventListener('click', categoryAction(async () => {
          modal.classList.add('hidden');
          const unlocked = await waitForCatlockUnlock();
          if (unlocked && state.currentNotebook) await loadAll();
        }));
      }
      $('settings-catlock-remove').addEventListener('click', categoryAction(async () => {
        modal.classList.add('hidden');
        if (!state.catlockSessionUnlocked) {
          const unlocked = await waitForCatlockUnlock();
          if (!unlocked) return;
        }
        const ok = await confirmDialog({
          title: 'Remove category lock?',
          message: 'All categories will be unlocked and the PIN will be removed.',
          confirmLabel: 'Remove PIN',
        });
        if (!ok) return;
        await api('POST', '/auth/catlock/remove', {});
        state.catlockConfigured = false;
        state.catlockSessionUnlocked = false;
        $('catlock-lock-btn').classList.add('hidden');
        modal.classList.add('hidden');
        if (state.currentNotebook) await loadAll();
        toast('Category lock removed');
      }));
    } else {
      catlockStatusEl.innerHTML = `<div class="settings-vault-row">
        <p>No category lock configured.</p>
        <button id="settings-catlock-enable" class="primary">Set PIN</button>
      </div>`;
      $('settings-catlock-enable').addEventListener('click', async () => {
        modal.classList.add('hidden');
        await openCatlockSetup();
      });
    }
  } catch {
    catlockStatusEl.innerHTML = `<p class="hint">Could not load category lock status.</p>`;
  }
}

function toast(msg: string, isError = false) {
  const container = $('toast');
  if (Array.from(container.children).some(el => el.textContent === msg)) return;
  const el = document.createElement('div');
  el.className = 'toast-item' + (isError ? ' error' : '');
  el.textContent = msg;
  container.appendChild(el);
  setTimeout(() => el.remove(), 5000);
}
const ukDateTimeFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
  timeZone: 'Europe/London',
});
function fmtUKDateTime(d: string): string {
  return ukDateTimeFmt.format(new Date(d)).replace(',', '');
}
function fmtRel(d: string): string {
  const dt = new Date(d);
  const diff = Date.now() - dt.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return fmtUKDateTime(d);
}
function catById(id: string | null): Collection | undefined {
  if (!id) return undefined;
  return state.collections.find(c => c.id === id);
}

function isNetworkError(e: unknown): boolean {
  return e instanceof TypeError;
}

function setOfflineMode(val: boolean) {
  if (val && vaultCacheDisabled()) { toast('Connection lost — keep this page open until your encrypted notes can be saved.', true); return; }
  state.offlineMode = val;
  document.body.classList.toggle('offline-mode', val);
  const ind = document.getElementById('offline-indicator');
  if (ind) {
    ind.classList.toggle('is-online', !val);
    ind.classList.toggle('is-offline', val);
    ind.textContent = val ? 'Offline' : 'Online';
  }
}

function loadFromCache(): boolean {
  const notebooks = loadCachedNotebooks<Notebook>();
  if (!notebooks || notebooks.length === 0) return false;
  state.notebooks = notebooks;
  const savedId = localStorage.getItem(NB_KEY);
  state.currentNotebook = state.notebooks.find(nb => nb.id === savedId) ?? state.notebooks[0] ?? null;
  if (!state.currentNotebook) return false;
  const nbId = state.currentNotebook.id;
  state.notes = loadCachedNotes<Note>(nbId) ?? [];
  state.collections = loadCachedCollections<Collection>(nbId) ?? [];
  renderNotebookSelector();
  renderSidebar();
  renderGrid();
  return true;
}

// Queue current editor content to the offline update queue (called before sync
// when a dirty note is open and we need to flush without hitting the server).
function queueCurrentEdit() {
  if (!state.currentId || !state.dirty) return;
  const id = state.currentId;
  if (state.saveTimer !== null) { window.clearTimeout(state.saveTimer); state.saveTimer = null; }
  const title = ($('note-title') as HTMLInputElement).value;
  const content_json = state.noteType === 'rich'
    ? JSON.stringify(state.quill?.getContents() ?? { ops: [{ insert: '\n' }] })
    : ($('note-source-textarea') as HTMLTextAreaElement).value;
  const collection_id = ($('note-cat') as HTMLSelectElement).value || null;
  const update: PendingUpdate = { id, title, content_json, collection_id, title_modified: state.titleEditedThisSession };
  queueUpdate(update);
  state.dirty = false;
  setSaveStatus('Queued for sync', false);
}

let pendingSync: Promise<void> | null = null;
function syncOfflineChanges(): Promise<void> {
  if (pendingSync) return pendingSync;
  pendingSync = performOfflineSync().finally(() => { pendingSync = null; });
  return pendingSync;
}

async function performOfflineSync() {
  if (pendingSave) await pendingSave;
  if (!hasPendingOps()) {
    try {
      state.notebooks = await api<Notebook[]>('GET', '/api/notebooks');
      persistNotebooks(state.notebooks);
      const savedId = localStorage.getItem(NB_KEY);
      state.currentNotebook = state.notebooks.find(nb => nb.id === savedId) ?? state.notebooks[0] ?? state.currentNotebook;
      if (state.currentNotebook) await loadAll();
    } catch { /* still spotty — silent */ }
    return;
  }

  toast('Syncing offline changes…');

  // Flush the open editor to the queue before syncing
  try { queueCurrentEdit(); }
  catch (e) {
    setSaveStatus('Save failed — keep this page open', true);
    toast(`Could not queue edits: ${(e as Error).message}`, true);
    return;
  }

  // If an offline-ID note is open, close it — after sync it'll have a real server ID
  if (state.currentId !== null && isOfflineId(state.currentId)) {
    state.currentId = null;
    state.attachments = [];
    state.titleEditedThisSession = false;
    $('editor-overlay').classList.add('hidden');
  }

  try {
    await flushOfflineQueue(api);
    state.notebooks = await api<Notebook[]>('GET', '/api/notebooks');
    persistNotebooks(state.notebooks);
    const savedId = localStorage.getItem(NB_KEY);
    state.currentNotebook = state.notebooks.find(nb => nb.id === savedId) ?? state.notebooks[0] ?? state.currentNotebook;
    if (state.currentNotebook) await loadAll();
    if (state.currentId && !state.dirty && !pendingSave && !notePendingStatus(state.currentId)) {
      setSaveStatus('Saved', false);
    }
    toast(hasPendingOps() ? 'Sync partially complete — some changes still pending' : 'All changes synced');
  } catch (e) {
    if ((e as Error).message === 'unauthenticated') {
      toast('Sign in again to sync your saved offline changes.', true);
      return;
    }
    toast(`Sync failed — changes still queued: ${(e as Error).message}`, true);
  }
}

// ----------------- Render: sidebar -----------------
// Permissive hex-color validator. Anything else is rejected and we fall
// back to the accent colour so untrusted input can never inject CSS.
function safeColor(input: string | null | undefined, fallback = '#7aa2f7'): string {
  if (!input) return fallback;
  return /^#[0-9a-fA-F]{6}$/.test(input) ? input : fallback;
}

function renderSidebar() {
  const list = $('categories-list');
  list.innerHTML = '';
  const sorted = [...state.collections].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  for (const c of sorted) {
    if (c.is_locked && state.catlockConfigured && !state.catlockSessionUnlocked) continue;
    const btn = document.createElement('button');
    btn.className = 'cat-item' + (state.currentFilter === c.id ? ' active' : '');
    btn.dataset.cat = c.id;
    const swatch = document.createElement('span');
    swatch.className = 'cat-swatch';
    swatch.style.background = safeColor(c.color);
    const nameEl = document.createElement('span');
    nameEl.className = 'cat-name';
    nameEl.textContent = c.name;
    btn.appendChild(swatch);
    btn.appendChild(nameEl);
    if (c.is_locked) {
      const lockEl = document.createElement('span');
      lockEl.className = 'cat-lock-icon';
      lockEl.textContent = '🔒';
      lockEl.title = 'Locked (unlocked this session)';
      btn.appendChild(lockEl);
    }
    btn.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('cat-edit')) return;
      setFilter(c.id);
    });
    btn.addEventListener('dblclick', () => openCatModal(c));
    btn.addEventListener('contextmenu', (e) => { e.preventDefault(); openCatModal(c); });
    list.appendChild(btn);
  }

  const live = state.notes.filter(n => !n.deleted_at);
  $('count-all').textContent = String(live.length);
  $('count-uncat').textContent = String(live.filter(n => !n.collection_id).length);

  const trashCountEl = $('count-trash');
  if (trashCountEl) trashCountEl.textContent = state.trashCount > 0 ? String(state.trashCount) : '';

  document.querySelectorAll('#sidebar .cat-item[data-cat="all"], #sidebar .cat-item[data-cat="uncategorized"], #sidebar .cat-item[data-cat="trash"]')
    .forEach((el) => {
      const k = (el as HTMLElement).dataset.cat!;
      el.classList.toggle('active', state.currentFilter === k);
    });
}

// ----------------- Render: grid -----------------
function filteredNotes(): Note[] {
  const f = (($('search') as HTMLInputElement).value || '').trim().toLowerCase();
  let items = state.notes.filter(n => !n.deleted_at);
  if (state.currentFilter === 'uncategorized') items = items.filter(n => !n.collection_id);
  else if (state.currentFilter !== 'all') items = items.filter(n => n.collection_id === state.currentFilter);
  if (f) {
    items = items.filter(n =>
      n.title.toLowerCase().includes(f) ||
      notePreviewText(n).toLowerCase().includes(f),
    );
  }
  items.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  return items;
}
function triggerGlobalSearch() {
  const q = (($('search') as HTMLInputElement).value || '').trim();
  if (!q) {
    state.globalSearchResults = null;
    renderGrid();
    return;
  }
  if (state.globalSearchTimer !== null) window.clearTimeout(state.globalSearchTimer);
  state.globalSearchResults = null; // show "Searching…" while waiting
  renderGrid();
  state.globalSearchTimer = window.setTimeout(async () => {
    state.globalSearchTimer = null;
    try {
      const results = await api<Note[]>('GET', `/api/notes/search?q=${encodeURIComponent(q)}`);
      state.globalSearchResults = results;
    } catch (e) {
      state.globalSearchResults = [];
      toast((e as Error).message || 'Search failed', true);
    }
    renderGrid();
  }, 300);
}

function setGlobalSearch(active: boolean) {
  state.globalSearch = active;
  state.globalSearchResults = null;
  const btn = $('global-search-btn');
  btn.classList.toggle('active', active);
  btn.setAttribute('aria-pressed', String(active));
  btn.textContent = `All notebooks: ${active ? 'On' : 'Off'}`;
  btn.title = active
    ? 'Search scope: all notebooks. Click to search only this notebook.'
    : 'Search scope: this notebook. Click to search all notebooks.';
  const search = $('search') as HTMLInputElement;
  search.placeholder = active ? 'Search all notebooks…' : 'Search this notebook…';
  search.setAttribute('aria-label', active ? 'Search all notebooks' : 'Search this notebook');
  if (active) {
    triggerGlobalSearch();
  } else {
    if (state.globalSearchTimer !== null) { window.clearTimeout(state.globalSearchTimer); state.globalSearchTimer = null; }
    renderGrid();
  }
}

function notebookById(id: string | null): Notebook | null {
  if (!id) return null;
  return state.notebooks.find(nb => nb.id === id) ?? null;
}

function renderGrid() {
  const grid = $('notes-grid');
  const empty = $('empty-grid');
  const newNoteBtn = $('new-note-btn');
  const emptyTrashBtn = $('empty-trash-btn');
  const viewToggle = document.getElementById('view-toggle');
  grid.innerHTML = '';

  const isTrash = state.currentFilter === 'trash';
  newNoteBtn.classList.toggle('hidden', isTrash);
  emptyTrashBtn.classList.toggle('hidden', !isTrash);
  if (viewToggle) viewToggle.classList.toggle('hidden', isTrash);
  grid.setAttribute('data-view', isTrash ? 'cards' : state.viewMode);
  const emptyMsg = document.getElementById('empty-grid-msg');
  const emptyNewBtn = $('empty-new-btn');
  if (emptyMsg) emptyMsg.textContent = isTrash ? 'Trash is empty.' : 'No notes here yet.';
  emptyNewBtn.classList.toggle('hidden', isTrash);

  if (isTrash) {
    $('current-cat-name').textContent = 'Trash';
    const items = state.trashNotes;
    if (!state.trashLoaded) {
      empty.classList.add('hidden');
      grid.classList.remove('hidden');
      const loading = document.createElement('p');
      loading.className = 'trash-loading';
      loading.textContent = 'Loading…';
      grid.appendChild(loading);
      return;
    }
    if (items.length === 0) {
      empty.classList.remove('hidden');
      grid.classList.add('hidden');
      return;
    }
    empty.classList.add('hidden');
    grid.classList.remove('hidden');
    for (const n of items) {
      const card = document.createElement('div');
      card.className = 'note-card note-card--trash';

      const title = document.createElement('div');
      title.className = 'card-title';
      title.textContent = n.title || 'Untitled';

      const preview = document.createElement('div');
      preview.className = 'card-preview';
      preview.textContent = notePreviewText(n).trim() || 'Empty note';

      const foot = document.createElement('div');
      foot.className = 'card-foot card-foot--trash';

      const deletedTime = document.createElement('span');
      deletedTime.textContent = n.deleted_at ? `Deleted ${fmtUKDateTime(n.deleted_at)}` : '';
      deletedTime.title = 'Date and time deleted';
      foot.appendChild(deletedTime);

      const actions = document.createElement('div');
      actions.className = 'trash-card-actions';

      const restoreBtn = document.createElement('button');
      restoreBtn.className = 'trash-restore-btn';
      restoreBtn.textContent = 'Restore';
      restoreBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        restoreBtn.disabled = true;
        restoreBtn.textContent = 'Restoring…';
        try {
          const restored = await api<Note>('POST', `/api/notes/${n.id}/restore`);
          state.trashNotes = state.trashNotes.filter(x => x.id !== n.id);
          state.trashCount = state.trashNotes.length;
          state.notes.unshift(restored);
          if (state.currentNotebook) persistNotes(state.currentNotebook.id, state.notes);
          renderSidebar();
          renderGrid();
          toast('Note restored');
        } catch (err) {
          restoreBtn.disabled = false;
          restoreBtn.textContent = 'Restore';
          toast(`Restore failed: ${(err as Error).message}`, true);
        }
      });

      const deleteForeverBtn = document.createElement('button');
      deleteForeverBtn.className = 'trash-delete-btn';
      deleteForeverBtn.textContent = 'Delete Forever';
      deleteForeverBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const ok = await confirmDialog({ title: 'Delete forever?', message: `"${n.title || 'Untitled'}" will be permanently deleted and cannot be recovered.`, confirmLabel: 'Delete Forever' });
        if (!ok) return;
        deleteForeverBtn.disabled = true;
        deleteForeverBtn.textContent = 'Deleting…';
        try {
          await api('DELETE', `/api/notes/trash/${n.id}`);
          state.trashNotes = state.trashNotes.filter(x => x.id !== n.id);
          state.trashCount = state.trashNotes.length;
          renderSidebar();
          renderGrid();
          toast('Note permanently deleted');
        } catch (err) {
          deleteForeverBtn.disabled = false;
          deleteForeverBtn.textContent = 'Delete Forever';
          toast(`Delete failed: ${(err as Error).message}`, true);
        }
      });

      actions.appendChild(restoreBtn);
      actions.appendChild(deleteForeverBtn);
      card.appendChild(title);
      card.appendChild(preview);
      card.appendChild(foot);
      card.appendChild(actions);
      grid.appendChild(card);
    }
    return;
  }

  // Normal (non-trash) rendering
  // Global search mode: use cached API results (may be null while loading)
  const isGlobal = state.globalSearch && (($('search') as HTMLInputElement).value || '').trim() !== '';
  const items = isGlobal ? (state.globalSearchResults ?? []) : filteredNotes();

  if (isGlobal) {
    $('current-cat-name').textContent = state.globalSearchResults === null ? 'Searching…' : 'All Notebooks';
  } else {
    let label = 'All Notes';
    if (state.currentFilter === 'uncategorized') label = 'Uncategorized';
    else {
      const c = catById(state.currentFilter);
      if (c) label = c.name;
    }
    $('current-cat-name').textContent = label;
  }

  if (items.length === 0 && state.globalSearchResults !== null) {
    empty.classList.remove('hidden');
    grid.classList.add('hidden');
    return;
  }
  if (!isGlobal && items.length === 0) {
    empty.classList.remove('hidden');
    grid.classList.add('hidden');
    return;
  }
  grid.classList.remove('hidden');
  empty.classList.add('hidden');

  for (const n of items) {
    const card = document.createElement('div');
    card.className = 'note-card';
    card.tabIndex = 0;
    card.setAttribute('role', 'button');
    card.setAttribute('aria-label', `Open ${n.title || 'Untitled'}`);
    card.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
    });
    card.addEventListener('click', () => {
      if (isGlobal && n.notebook_id && n.notebook_id !== state.currentNotebook?.id) {
        const nb = notebookById(n.notebook_id);
        if (nb) switchNotebook(nb).then(() => openEditor(n.id)).catch((e) => toast((e as Error).message, true));
      } else {
        openEditor(n.id);
      }
    });

    const title = document.createElement('div');
    title.className = 'card-title';
    title.textContent = n.title || 'Untitled';

    const preview = document.createElement('div');
    if (n.note_type && n.note_type !== 'rich') {
      preview.className = 'card-preview card-preview--type';
      const typeLabel = document.createElement('span');
      typeLabel.className = 'card-type-icon';
      typeLabel.textContent = n.note_type.toUpperCase();
      preview.appendChild(typeLabel);
    } else {
      preview.className = 'card-preview';
      preview.textContent = notePreviewText(n).trim() || 'Empty note';
    }

    const foot = document.createElement('div');
    foot.className = 'card-foot';
    const time = document.createElement('span');
    time.className = 'card-time';
    time.textContent = fmtUKDateTime(n.updated_at);
    time.title = 'Last updated';
    foot.appendChild(time);

    // In global search, show which notebook the note belongs to
    if (isGlobal) {
      const nb = notebookById(n.notebook_id ?? null);
      if (nb) {
        const nbLabel = document.createElement('span');
        nbLabel.className = 'card-notebook-label';
        nbLabel.textContent = nb.name;
        foot.appendChild(nbLabel);
      }
    } else {
      const c = catById(n.collection_id);
      if (c) {
        const cat = document.createElement('span');
        cat.className = 'card-cat';
        const sw = document.createElement('span');
        sw.className = 'cat-swatch';
        sw.style.background = safeColor(c.color);
        const nm = document.createElement('span');
        nm.textContent = c.name;
        cat.appendChild(sw);
        cat.appendChild(nm);
        foot.appendChild(cat);
      }
    }

    const pending = notePendingStatus(n.id);
    if (pending) {
      const badge = document.createElement('span');
      badge.className = 'card-pending-badge';
      badge.title = pending === 'create' ? 'This note has not been synced to the server yet' : 'This note has unsaved changes waiting to sync';
      badge.textContent = pending === 'create' ? 'Not synced' : 'Pending sync';
      foot.appendChild(badge);
    }

    card.appendChild(title);
    card.appendChild(preview);
    card.appendChild(foot);
    grid.appendChild(card);
  }
}

// ----------------- Editor -----------------
function renderCatSelect(selected: string | null) {
  const sel = $('note-cat') as HTMLSelectElement;
  sel.innerHTML = '';
  const none = document.createElement('option');
  none.value = '';
  none.textContent = '— Uncategorized —';
  sel.appendChild(none);
  const sorted = [...state.collections]
    .filter(c => !(c.is_locked && state.catlockConfigured && !state.catlockSessionUnlocked))
    .sort((a, b) => a.name.localeCompare(b.name));
  for (const c of sorted) {
    const opt = document.createElement('option');
    opt.value = c.id;
    opt.textContent = c.name;
    sel.appendChild(opt);
  }
  sel.value = selected || '';
}

function ensureQuill(): Quill {
  if (state.quill) return state.quill;
  const q = new Quill('#note-body', {
    theme: 'snow',
    placeholder: 'Write…',
    modules: {
      toolbar: {
        container: '#editor-toolbar',
        handlers: {
          image: () => triggerImagePicker(),
        },
      },
    },
  });
  q.on('text-change', (_d, _o, source) => {
    if (source === 'user' && !state.suppressQuillChange) scheduleSave();
  });
  const root = q.root as HTMLElement;
  root.addEventListener('paste', onEditorPaste, true);
  root.addEventListener('drop', onEditorDrop, true);
  root.addEventListener('dragover', (e) => e.preventDefault());
  installPencilHighlighter(q);
  installPlainTextCopy(q, $('copy-plain-text'), toast);
  state.quill = q;
  return q;
}

function parseDelta(json: string): unknown {
  try {
    const d = JSON.parse(json);
    if (d && Array.isArray(d.ops)) return d;
  } catch { /* ignore */ }
  return { ops: [{ insert: '\n' }] };
}

function exitPreviewMode() {
  if (!state.previewMode) return;
  state.previewMode = false;
  if (state.noteType === 'rich') {
    $('editor-toolbar').classList.remove('hidden');
    $('note-body').classList.remove('hidden');
  } else {
    $('note-source-container').classList.remove('hidden');
  }
  const preview = $('note-preview');
  preview.innerHTML = '';
  preview.classList.add('hidden');
  preview.classList.remove('iframe-preview');
  if (state.iframeBlobUrl) {
    URL.revokeObjectURL(state.iframeBlobUrl);
    state.iframeBlobUrl = null;
  }
  $('tab-edit').classList.add('active');
  $('tab-preview').classList.remove('active');
}

function togglePreview(anchor = '') {
  if (state.previewMode) { exitPreviewMode(); return; }
  state.previewMode = true;
  {
    const preview = $('note-preview');
    if (state.noteType === 'rich') {
      const q = state.quill;
      if (!q) { state.previewMode = false; return; }
      preview.innerHTML = renderMarkdown(q.getText());
      $('editor-toolbar').classList.add('hidden');
      $('note-body').classList.add('hidden');
    } else if (state.noteType === 'markdown') {
      const ta = $('note-source-textarea') as HTMLTextAreaElement;
      preview.innerHTML = renderMarkdown(ta.value);
      $('note-source-container').classList.add('hidden');
    } else {
      // HTML — POST content to /api/html-preview which returns the HTML with
      // its own permissive CSP. Blob URLs inherit the parent page's strict CSP
      // and block inline scripts, external fonts, and cross-origin base URIs.
      const ta = $('note-source-textarea') as HTMLTextAreaElement;
      if (state.iframeBlobUrl) { URL.revokeObjectURL(state.iframeBlobUrl); state.iframeBlobUrl = null; }
      const iframeName = 'html-preview-' + Date.now();
      const iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.sandbox.add('allow-scripts');
      iframe.sandbox.add('allow-popups');
      iframe.sandbox.add('allow-popups-to-escape-sandbox');
      preview.innerHTML = '';
      preview.appendChild(iframe);
      preview.classList.add('iframe-preview');
      $('note-source-container').classList.add('hidden');
      // Submit via a hidden form so the iframe navigates to a real URL whose
      // response headers override the parent CSP.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/html-preview';
      form.target = iframeName;
      form.style.cssText = 'position:absolute;width:0;height:0;opacity:0;pointer-events:none;';
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'content';
      input.value = injectBase(ta.value);
      form.appendChild(input);
      if (anchor) {
        const anchorInput = document.createElement('input');
        anchorInput.type = 'hidden';
        anchorInput.name = 'anchor';
        anchorInput.value = anchor;
        form.appendChild(anchorInput);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }
    preview.classList.remove('hidden');
    $('tab-edit').classList.remove('active');
    $('tab-preview').classList.add('active');
  }
}

function updateBackButton() {
  const btn = $('tab-back');
  if (state.noteHistory.length > 0) btn.classList.remove('hidden');
  else btn.classList.add('hidden');
}

async function openEditor(id: string, keepHistory = false) {
  if (state.currentId && state.currentId !== id) {
    await closeEditor();
    if (state.currentId) return;
  }
  if (!keepHistory) { state.noteHistory = []; updateBackButton(); }
  // Revoke any leftover iframe blob from a previous note
  if (state.iframeBlobUrl) {
    URL.revokeObjectURL(state.iframeBlobUrl);
    state.iframeBlobUrl = null;
  }
  exitPreviewMode();
  state.currentId = id;
  const note = state.notes.find(n => n.id === id);
  if (!note) return;
  state.noteType = note.note_type || 'rich';
  ($('note-title') as HTMLInputElement).value = note.title;
  state.titleEditedThisSession = false;

  if (state.noteType === 'rich') {
    $('editor-toolbar').classList.remove('hidden');
    $('note-body').classList.remove('hidden');
    $('note-source-container').classList.add('hidden');
    const q = ensureQuill();
    state.suppressQuillChange = true;
    q.setContents(parseDelta(note.content_json) as any, 'silent');
    state.suppressQuillChange = false;
  } else {
    $('editor-toolbar').classList.add('hidden');
    $('note-body').classList.add('hidden');
    $('note-source-container').classList.remove('hidden');
    ($('note-source-textarea') as HTMLTextAreaElement).value = note.content_json;
    ($('source-file-input') as HTMLInputElement).accept =
      state.noteType === 'markdown' ? '.md,.txt,text/markdown,text/plain' : '.html,.htm,text/html';
  }

  if (isOfflineId(id)) {
    setSaveStatus('Saved offline', false);
  } else {
    setSaveStatus(`Saved ${fmtRel(note.updated_at)}`, false);
  }
  renderCatSelect(note.collection_id);
  renderReminders();
  ($('editor-panel') as HTMLElement).style.width = '';
  ($('editor-panel') as HTMLElement).style.height = '';
  $('editor-overlay').classList.remove('hidden');
  ($('note-title') as HTMLInputElement).focus();
  // HTML and Markdown notes open in preview mode by default since the raw
  // source is not what the user normally wants to read.
  if (state.noteType === 'html' || state.noteType === 'markdown') {
    togglePreview();
  }
  await loadAttachments();
}

function openMoveDialog() {
  const note = state.notes.find(n => n.id === state.currentId);
  if (!note) return;
  const destinations = state.notebooks.filter(nb => nb.id !== note.notebook_id);
  if (!destinations.length) { toast('Create another notebook first.'); return; }
  if (state.offlineMode || isOfflineId(note.id)) { toast('Connect and sync this note before moving it.', true); return; }
  const dialog = document.createElement('dialog');
  dialog.className = 'move-dialog';
  dialog.setAttribute('aria-labelledby', 'move-heading');
  dialog.innerHTML = '<h2 id="move-heading">Move to notebook</h2><p>Your note, attachments and reminders move together. Its category will be cleared.</p><label for="move-destination">Destination notebook</label><select id="move-destination"></select><p class="error" role="alert"></p><div class="modal-actions"><button class="ghost" type="button">Cancel</button><button class="primary" type="button">Move note</button></div>';
  const select = dialog.querySelector('select')!;
  for (const nb of destinations) select.add(new Option(nb.name, nb.id));
  const cancel = dialog.querySelector<HTMLButtonElement>('button.ghost')!;
  const move = dialog.querySelector<HTMLButtonElement>('button.primary')!;
  let moving = false;
  cancel.onclick = () => dialog.close();
  dialog.addEventListener('keydown', e => e.stopPropagation());
  dialog.addEventListener('cancel', e => { if (moving) e.preventDefault(); });
  dialog.addEventListener('close', () => {
    dialog.remove();
    (state.currentId ? $('move-note-btn') : $('new-note-btn')).focus();
  });
  move.onclick = async () => {
    moving = true;
    move.disabled = cancel.disabled = select.disabled = true;
    move.textContent = 'Moving…';
    try {
      if (!await finishDrawMode()) throw new Error('Save your drawing before moving this note.');
      await flushSave();
      if (state.offlineMode || notePendingStatus(note.id)) throw new Error('Sync pending edits before moving this note.');
      const destination = destinations.find(nb => nb.id === select.value)!;
      const moved = await api<Note>('PUT', `/api/notes/${encodeURIComponent(note.id)}/notebook`, { notebook_id: destination.id });
      state.notes = state.notes.filter(n => n.id !== note.id);
      if (state.currentNotebook) persistNotes(state.currentNotebook.id, state.notes);
      const cached = loadCachedNotes<Note>(destination.id) ?? [];
      persistNotes(destination.id, [moved, ...cached.filter(n => n.id !== moved.id)]);
      state.globalSearchResults = null;
      await closeEditor();
      renderSidebar();
      renderGrid();
      dialog.close();
      toast(`Moved to ${destination.name}`);
    } catch (error) {
      dialog.querySelector('.error')!.textContent = (error as Error).message;
    } finally {
      moving = false;
      move.disabled = cancel.disabled = select.disabled = false;
      move.textContent = 'Move note';
    }
  };
  document.body.appendChild(dialog);
  dialog.showModal();
  select.focus();
}

async function closeEditor() {
  if (isInkModalOpen() && !await dismissInkModal()) return;
  if (!await finishDrawMode()) return;
  // Cancel any pending debounced save and flush synchronously, so a save
  // that lands after the editor is gone doesn't lose state on failure.
  if (state.saveTimer !== null) {
    window.clearTimeout(state.saveTimer);
    state.saveTimer = null;
  }
  const idAtClose = state.currentId;
  if (state.dirty || pendingSave) {
    try {
      await flushSave();
    } catch {
      /* flushSave handles its own toast + dirty re-set */
    }
  }
  // If the save failed, flushSave re-set state.dirty and we still know which
  // note was affected. Re-open the editor for that note so the user can
  // recover, instead of silently abandoning their edits.
  if (state.dirty && idAtClose && state.notes.find(n => n.id === idAtClose)) {
    toast('Editor reopened: save failed, please retry.', true);
    state.currentId = idAtClose;
    return;
  }
  state.currentId = null;
  state.attachments = [];
  state.titleEditedThisSession = false;
  if (state.iframeBlobUrl) {
    URL.revokeObjectURL(state.iframeBlobUrl);
    state.iframeBlobUrl = null;
  }
  state.noteType = 'rich';
  $('editor-overlay').classList.add('hidden');
}

function renderReminders() {
  const panel = $('reminders-panel');
  panel.innerHTML = '';
  const mine = state.reminders.filter(r => r.note_id === state.currentId);
  for (const r of mine) {
    const div = document.createElement('div');
    div.className = 'reminder';
    const when = fmtUKDateTime(r.trigger_time);
    div.textContent = `⏰ ${when}`;
    const btn = document.createElement('button');
    btn.textContent = '✕';
    btn.title = 'Remove reminder';
    btn.addEventListener('click', () => deleteReminder(r.id));
    div.appendChild(btn);
    panel.appendChild(div);
  }
}

// ----------------- Attachments -----------------
async function loadAttachments() {
  if (!state.currentId) { state.attachments = []; renderAttachments(); return; }
  if (isOfflineId(state.currentId)) { state.attachments = []; renderAttachments(); return; }
  try {
    state.attachments = await api<Attachment[]>('GET', `/api/notes/${state.currentId}/attachments`);
  } catch (e) {
    state.attachments = [];
    logWarn('attachments load failed', e);
  }
  renderAttachments();
}

function renderAttachments() {
  const list = $('attachments-list');
  list.innerHTML = '';
  if (state.attachments.length === 0) {
    const hint = document.createElement('div');
    hint.className = 'att-empty';
    hint.textContent = 'No attachments. Drop or paste files into the note, or use the attach button.';
    list.appendChild(hint);
    return;
  }
  for (const a of state.attachments) {
    const row = document.createElement('div');
    row.className = 'att-row';
    const isImg = a.mime_type.startsWith('image/');
    const thumb = document.createElement(isImg ? 'img' : 'span');
    if (isImg) {
      (thumb as HTMLImageElement).src = blobUrl(a.blob_hash);
      thumb.className = 'att-thumb';
    } else {
      thumb.className = 'att-icon';
      thumb.innerHTML = fileIconSvg();
    }
    const meta = document.createElement('div');
    meta.className = 'att-meta';
    const n = document.createElement('div');
    n.className = 'att-filename';
    n.textContent = a.filename;
    n.title = a.filename;
    const s = document.createElement('div');
    s.className = 'att-size';
    s.textContent = `${formatFileSize(a.size)} · ${a.mime_type}`;
    meta.appendChild(n);
    meta.appendChild(s);

    const actions = document.createElement('div');
    actions.className = 'att-actions';
    const dl = document.createElement('a');
    dl.href = downloadUrl(a.blob_hash, a.filename);
    dl.download = a.filename;
    dl.className = 'icon-btn';
    dl.title = 'Download';
    dl.textContent = '⤓';
    const ins = document.createElement('button');
    ins.className = 'icon-btn';
    ins.title = isImg ? 'Insert image into note' : 'Insert link into note';
    ins.textContent = '↳';
    ins.addEventListener('click', () => insertAttachmentIntoEditor(a));
    const del = document.createElement('button');
    del.className = 'icon-btn danger';
    del.title = 'Delete attachment';
    del.textContent = '🗑';
    del.addEventListener('click', () => removeAttachment(a));
    actions.appendChild(dl);
    actions.appendChild(ins);
    actions.appendChild(del);

    row.appendChild(thumb);
    row.appendChild(meta);
    row.appendChild(actions);
    list.appendChild(row);
  }
}

function insertAttachmentIntoEditor(a: Attachment) {
  const q = state.quill;
  if (!q) return;
  const sel = q.getSelection(true);
  const idx = sel ? sel.index : q.getLength();
  if (a.mime_type.startsWith('image/')) {
    q.insertEmbed(idx, 'attachment-image', {
      attachmentId: a.id, blobHash: a.blob_hash, mimeType: a.mime_type, filename: a.filename,
    }, 'user');
  } else {
    q.insertEmbed(idx, 'attachment-file', {
      attachmentId: a.id, blobHash: a.blob_hash, mimeType: a.mime_type, filename: a.filename, size: a.size,
    }, 'user');
  }
  q.setSelection(idx + 1, 0, 'silent');
  scheduleSave();
}

async function uploadAttachment(file: File): Promise<Attachment | null> {
  if (!state.currentId) return null;
  const fd = new FormData();
  fd.append('file', file, file.name);
  try {
    const res = await fetch(`/api/notes/${state.currentId}/attachments`, {
      method: 'POST', credentials: 'same-origin', body: fd,
    });
    if (!res.ok) {
      let msg = `${res.status}`;
      try { const j = await res.json(); const extracted = extractApiError(j); if (extracted) msg = extracted; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const a = await res.json() as Attachment;
    state.attachments.unshift(a);
    renderAttachments();
    return a;
  } catch (e) {
    toast(`Upload failed: ${(e as Error).message}`, true);
    return null;
  }
}

async function uploadAndInsert(file: File, insertInline: boolean) {
  const a = await uploadAttachment(file);
  if (a && insertInline) insertAttachmentIntoEditor(a);
}

async function removeAttachment(a: Attachment) {
  if (state.offlineMode) { toast('Attachments require a connection', true); return; }
  const ok = await confirmDialog({
    title: 'Delete attachment?',
    message: `"${a.filename}" will be removed. Any inline reference in the note will break.`,
    confirmLabel: 'Delete',
  });
  if (!ok) return;
  try {
    await api('DELETE', `/api/attachments/${a.id}`);
    state.attachments = state.attachments.filter(x => x.id !== a.id);
    renderAttachments();
    toast('Attachment deleted');
  } catch (e) {
    toast((e as Error).message, true);
  }
}

function triggerImagePicker() {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = 'image/*';
  inp.onchange = async () => {
    const f = inp.files?.[0];
    if (f) await uploadAndInsert(f, true);
  };
  inp.click();
}

function onEditorPaste(e: ClipboardEvent) {
  if (state.offlineMode) { toast('Attachments require a connection', true); return; }
  const items = e.clipboardData?.items;
  if (!items) return;
  const files: File[] = [];
  for (const it of Array.from(items)) {
    if (it.kind === 'file') {
      const f = it.getAsFile();
      if (f) files.push(f);
    }
  }
  if (files.length === 0) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  (async () => {
    for (const f of files) {
      // Mirror the drop behaviour: images go inline, everything else
      // becomes a sidebar attachment so users don't lose pasted PDFs etc.
      const inline = f.type.startsWith('image/');
      await uploadAndInsert(f, inline);
    }
  })();
}

function onEditorDrop(e: DragEvent) {
  if (state.offlineMode) { toast('Attachments require a connection', true); return; }
  const files = e.dataTransfer?.files;
  if (!files || files.length === 0) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  (async () => {
    for (const f of Array.from(files)) {
      const inline = f.type.startsWith('image/');
      await uploadAndInsert(f, inline);
    }
  })();
}

// ----------------- Actions -----------------
function setFilter(f: string) {
  state.currentFilter = f;
  renderSidebar();
  if (f === 'trash' && !state.trashLoaded) {
    loadTrashNotes();
  } else {
    renderGrid();
  }
}
async function loadAll() {
  const nbId = state.currentNotebook!.id;
  const [notes, cols, trashData] = await Promise.all([
    api<Note[]>('GET', `/api/notes?notebook_id=${encodeURIComponent(nbId)}`),
    api<Collection[]>('GET', `/api/collections?notebook_id=${encodeURIComponent(nbId)}`),
    api<{ count: number }>('GET', `/api/notes/trash/count?notebook_id=${encodeURIComponent(nbId)}`).catch(() => ({ count: 0 })),
  ]);
  state.notes = mergePendingNotes(nbId, notes);
  state.collections = cols;
  state.trashCount = trashData.count;
  state.trashLoaded = false;
  state.trashNotes = [];
  if (state.currentFilter === 'trash') {
    state.trashNotes = await api<Note[]>('GET', `/api/notes/trash?notebook_id=${encodeURIComponent(nbId)}`).catch(() => []);
    state.trashLoaded = true;
    state.trashCount = state.trashNotes.length;
  }
  persistNotes(nbId, state.notes);
  persistCollections(nbId, cols);
  renderSidebar();
  renderGrid();
}
async function loadTrashNotes() {
  if (!state.currentNotebook) return;
  const nbId = state.currentNotebook.id;
  try {
    state.trashNotes = await api<Note[]>('GET', `/api/notes/trash?notebook_id=${encodeURIComponent(nbId)}`);
    state.trashCount = state.trashNotes.length;
    state.trashLoaded = true;
  } catch (e) {
    logWarn('trash load failed', e);
    state.trashNotes = [];
  }
  renderSidebar();
  renderGrid();
}

async function loadReminders() {
  try {
    state.reminders = await api<Reminder[]>('GET', '/api/reminders');
    if (state.currentId) renderReminders();
  } catch (e) { logWarn('reminders load failed', e); }
}
function buildOfflineNote(noteType: string, collection_id: string | null, initialContent: string): Note {
  const now = new Date().toISOString();
  return {
    id: `offline-${crypto.randomUUID()}`,
    title: 'New note',
    content_json: initialContent,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    title_modified: false,
    collection_id,
    notebook_id: state.currentNotebook?.id ?? null,
    note_type: noteType,
  };
}

async function createNote() {
  const noteType = await pickNoteType();
  if (!noteType) return;
  const collection_id = (state.currentFilter !== 'all' && state.currentFilter !== 'uncategorized')
    ? state.currentFilter : null;
  const initialContent = noteType === 'rich' ? JSON.stringify({ ops: [{ insert: '\n' }] }) : '';
  const draft = buildOfflineNote(noteType, collection_id, initialContent);

  if (state.offlineMode) {
    const n = draft;
    queueCreate({ clientId: n.id, title: n.title, content_json: n.content_json, collection_id, notebook_id: state.currentNotebook!.id, note_type: noteType });
    state.notes.unshift(n);
    persistNotes(state.currentNotebook!.id, state.notes);
    renderSidebar();
    renderGrid();
    await openEditor(n.id);
    return;
  }

  try {
    const n = await api<Note>('POST', '/api/notes', {
      client_id: draft.id.slice('offline-'.length),
      title: 'New note', content_json: initialContent, collection_id,
      notebook_id: state.currentNotebook!.id, note_type: noteType,
    });
    state.notes.unshift(n);
    persistNotes(state.currentNotebook!.id, state.notes);
    renderSidebar();
    renderGrid();
    await openEditor(n.id);
  } catch (e) {
    if (isNetworkError(e)) {
      setOfflineMode(true);
      const n = draft;
      queueCreate({ clientId: n.id, attempted: true, title: n.title, content_json: n.content_json, collection_id, notebook_id: state.currentNotebook!.id, note_type: noteType });
      state.notes.unshift(n);
      persistNotes(state.currentNotebook!.id, state.notes);
      renderSidebar();
      renderGrid();
      await openEditor(n.id);
    } else {
      toast(`Create failed: ${(e as Error).message}`, true);
    }
  }
}
async function deleteCurrent() {
  if (!state.currentId) return;
  const id = state.currentId;
  const isTemp = isOfflineId(id);
  const ok = await confirmDialog({
    title: 'Delete note?',
    message: isTemp
      ? 'This offline note will be discarded.'
      : 'This note will be moved to trash. You can restore it or empty trash to permanently delete.',
    confirmLabel: 'Delete',
  });
  if (!ok) return;

  let queued = false;
  if (isTemp) {
    cancelCreate(id);
  } else if (state.offlineMode) {
    queueDelete(id);
    queued = true;
  } else {
    try {
      await api('DELETE', `/api/notes/${id}`);
    } catch (e) {
      if (isNetworkError(e)) {
        setOfflineMode(true);
        queueDelete(id);
        queued = true;
      } else {
        toast(`Delete failed: ${(e as Error).message}`, true);
        return;
      }
    }
  }

  const deletedNote = state.notes.find(n => n.id === id);
  state.notes = state.notes.filter(n => n.id !== id);
  if (state.currentNotebook) persistNotes(state.currentNotebook.id, state.notes);
  if (!isTemp && !queued && deletedNote) {
    const now = new Date().toISOString();
    state.trashNotes = [{ ...deletedNote, deleted_at: now }, ...state.trashNotes];
    state.trashCount = state.trashNotes.length;
    state.trashLoaded = true;
  }
  if (state.saveTimer !== null) { window.clearTimeout(state.saveTimer); state.saveTimer = null; }
  state.dirty = false;
  deactivateDrawMode();
  await closeEditor();
  renderSidebar();
  renderGrid();
  toast(isTemp ? 'Note discarded' : queued ? 'Note queued for deletion' : 'Note moved to trash');
}
function setSaveStatus(text: string, dirty: boolean) {
  const el = $('save-status');
  el.textContent = text;
  el.classList.toggle('unsaved', dirty);
}
function scheduleSave() {
  state.dirty = true;
  setSaveStatus('Unsaved changes…', true);
  if (state.saveTimer !== null) window.clearTimeout(state.saveTimer);
  state.saveTimer = window.setTimeout(() => {
    flushSave().catch(() => { /* surfaced via toast in flushSave */ });
  }, 600);
}
function saveOffline(id: string, title: string, content_json: string, collection_id: string | null, title_modified: boolean) {
  queueUpdate({ id, title, content_json, collection_id, title_modified });
  const idx = state.notes.findIndex(n => n.id === id);
  const existing = idx >= 0 ? state.notes[idx] : undefined;
  if (existing !== undefined) {
    state.notes[idx] = { ...existing, title, content_json, collection_id, updated_at: new Date().toISOString() };
  }
  if (state.currentNotebook) persistNotes(state.currentNotebook.id, state.notes);
  setSaveStatus('Saved offline', false);
  renderSidebar();
  renderGrid();
}

let pendingSave: Promise<void> | null = null;
async function flushSave(): Promise<void> {
  // Serialize saves and drain edits made while a request was outstanding.
  // Closing or moving a note must await the same operation as autosave.
  while (pendingSave) await pendingSave;
  if (!state.currentId || !state.dirty) return;
  if (state.saveTimer !== null) {
    window.clearTimeout(state.saveTimer);
    state.saveTimer = null;
  }
  const operation = saveCurrentNote();
  pendingSave = operation;
  try { await operation; }
  finally { if (pendingSave === operation) pendingSave = null; }
  if (state.dirty) await flushSave();
}

async function saveCurrentNote() {
  if (!state.currentId || !state.dirty) return;
  const id = state.currentId;
  const title = ($('note-title') as HTMLInputElement).value;
  let content_json: string;
  if (state.noteType === 'rich') {
    const q = state.quill;
    content_json = JSON.stringify(q ? q.getContents() : { ops: [{ insert: '\n' }] });
  } else {
    content_json = ($('note-source-textarea') as HTMLTextAreaElement).value;
  }
  const selectedCat = ($('note-cat') as HTMLSelectElement).value || null;
  const titleEdited = state.titleEditedThisSession;
  state.dirty = false;
  state.saveTimer = null;

  try {
    if (state.offlineMode || isOfflineId(id) || notePendingStatus(id)) {
      saveOffline(id, title, content_json, selectedCat, titleEdited);
      return;
    }
    try {
      const body: Record<string, unknown> = { title, content_json };
      // Only flip title_modified when the user actually edited the title in
      // this session. Otherwise leave the server-side value alone (PUT body
      // omits the field).
      if (titleEdited) body.title_modified = true;
      const updated = await api<Note>('PUT', `/api/notes/${id}`, body);
      const orig = state.notes.find(n => n.id === id);
      if (orig && orig.collection_id !== selectedCat) {
        await api('PUT', `/api/notes/${id}/collection`, { collection_id: selectedCat });
        updated.collection_id = selectedCat;
      }
      const idx = state.notes.findIndex(n => n.id === id);
      if (idx >= 0) state.notes[idx] = updated;
      if (state.currentNotebook) persistNotes(state.currentNotebook.id, state.notes);
      clearQueuedUpdate(id);
      setSaveStatus(`Saved ${fmtRel(updated.updated_at)}`, false);
      renderSidebar();
      renderGrid();
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      setOfflineMode(true);
      saveOffline(id, title, content_json, selectedCat, titleEdited);
    }
  } catch (e) {
    toast(`Save failed: ${(e as Error).message}`, true);
    setSaveStatus(`Save failed — will retry`, true);
    state.dirty = true;
    throw e;
  }
}
async function addReminder() {
  if (!state.currentId) return;
  openReminderModal();
}
async function deleteReminder(id: string) {
  try {
    await api('DELETE', `/api/reminders/${id}`);
    state.reminders = state.reminders.filter(r => r.id !== id);
    renderReminders();
  } catch (e) {
    toast(`Failed to delete reminder: ${(e as Error).message}`, true);
  }
}

// ----------------- Category modal -----------------
function openCatModal(existing?: Collection) {
  state.editingCatId = existing ? existing.id : null;
  $('cat-modal-title').textContent = existing ? 'Edit category' : 'New category';
  ($('cat-name-input') as HTMLInputElement).value = existing ? existing.name : '';
  ($('cat-color-input') as HTMLInputElement).value = existing ? existing.color : '#7aa2f7';
  $('cat-delete').classList.toggle('hidden', !existing);
  const lockRow = $('cat-lock-row');
  const lockInput = $('cat-lock-input') as HTMLInputElement;
  if (existing) {
    lockRow.classList.remove('hidden');
    lockInput.checked = existing.is_locked ?? false;
  } else {
    lockRow.classList.add('hidden');
    lockInput.checked = false;
  }
  $('cat-modal').classList.remove('hidden');
  ($('cat-name-input') as HTMLInputElement).focus();
}
function closeCatModal() {
  state.editingCatId = null;
  $('cat-modal').classList.add('hidden');
}
let saveCatInflight = false;
async function saveCat() {
  if (saveCatInflight) return;
  saveCatInflight = true;
  const name = ($('cat-name-input') as HTMLInputElement).value.trim();
  const color = ($('cat-color-input') as HTMLInputElement).value;
  if (!name) { saveCatInflight = false; toast('Name required', true); return; }
  try {
    if (state.editingCatId) {
      const isLocked = ($('cat-lock-input') as HTMLInputElement).checked;
      if (isLocked) {
        if (!state.catlockConfigured) {
          await openCatlockSetup();
          if (!state.catlockConfigured) { saveCatInflight = false; return; }
        } else if (!state.catlockSessionUnlocked) {
          const ok = await waitForCatlockUnlock();
          if (!ok) { saveCatInflight = false; return; }
        }
      }
      const catId = state.editingCatId;
      const c = await api<Collection>('PUT', `/api/collections/${catId}`, {
        id: catId, name, color, is_locked: isLocked,
      });
      const idx = state.collections.findIndex(x => x.id === c.id);
      if (idx >= 0) state.collections[idx] = c;
      // auto-lock session so the newly-locked category is hidden immediately
      if (isLocked) {
        await api('POST', '/auth/catlock/lock', {});
        state.catlockSessionUnlocked = false;
        $('catlock-lock-btn').classList.add('hidden');
        if (state.currentFilter === catId) state.currentFilter = 'all';
        closeCatModal();
        if (state.currentNotebook) await loadAll();
        return;
      }
    } else {
      const c = await api<Collection>('POST', '/api/collections', { name, color, notebook_id: state.currentNotebook!.id });
      state.collections.push(c);
    }
    closeCatModal();
    renderSidebar();
    renderGrid();
  } catch (e) { toast((e as Error).message, true); }
  finally { saveCatInflight = false; }
}
async function deleteCat() {
  if (!state.editingCatId) return;
  const ok = await confirmDialog({
    title: 'Delete category?',
    message: 'Notes inside will be moved to Uncategorized. The category itself will be removed.',
    confirmLabel: 'Delete category',
  });
  if (!ok) return;
  const id = state.editingCatId;
  try {
    await api('DELETE', `/api/collections/${id}`);
    state.collections = state.collections.filter(c => c.id !== id);
    state.notes = state.notes.map(n => n.collection_id === id ? { ...n, collection_id: null } : n);
    if (state.currentFilter === id) state.currentFilter = 'all';
    closeCatModal();
    renderSidebar();
    renderGrid();
  } catch (e) { toast((e as Error).message, true); }
}

// ----------------- Reminder modal -----------------
function pad2(n: number): string { return n < 10 ? '0' + n : String(n); }
function toLocalInputs(d: Date): { date: string; time: string } {
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
}
function updateRemPreview() {
  const d = ($('rem-date') as HTMLInputElement).value;
  const t = ($('rem-time') as HTMLInputElement).value;
  const preview = $('rem-preview');
  if (!d || !t) { preview.textContent = ''; return; }
  const dt = new Date(`${d}T${t}`);
  if (isNaN(dt.getTime())) { preview.textContent = 'Invalid date/time'; return; }
  const diffMs = dt.getTime() - Date.now();
  if (diffMs < 0) { preview.textContent = '⚠ That time is in the past'; return; }
  const mins = Math.round(diffMs / 60000);
  let rel: string;
  if (mins < 60) rel = `in ${mins} min`;
  else if (mins < 60 * 24) rel = `in ${Math.round(mins / 60)} hr`;
  else rel = `in ${Math.round(mins / (60 * 24))} days`;
  preview.textContent = `Fires ${rel} — ${fmtUKDateTime(dt.toISOString())}`;
}
function applyRemPreset(preset: string) {
  const now = new Date();
  let target: Date;
  if (preset === '1h') target = new Date(now.getTime() + 60 * 60 * 1000);
  else if (preset === '3h') target = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  else if (preset === 'tomorrow') {
    target = new Date(now); target.setDate(target.getDate() + 1);
    target.setHours(9, 0, 0, 0);
  } else if (preset === 'nextweek') {
    target = new Date(now);
    const dow = target.getDay();
    const add = ((8 - dow) % 7) || 7;
    target.setDate(target.getDate() + add);
    target.setHours(9, 0, 0, 0);
  } else return;
  const { date, time } = toLocalInputs(target);
  ($('rem-date') as HTMLInputElement).value = date;
  ($('rem-time') as HTMLInputElement).value = time;
  updateRemPreview();
}
function openReminderModal() {
  const def = new Date(Date.now() + 60 * 60 * 1000);
  const { date, time } = toLocalInputs(def);
  ($('rem-date') as HTMLInputElement).value = date;
  ($('rem-time') as HTMLInputElement).value = time;
  updateRemPreview();
  $('reminder-modal').classList.remove('hidden');
  ($('rem-date') as HTMLInputElement).focus();
}
function closeReminderModal() { $('reminder-modal').classList.add('hidden'); }
let saveReminderInflight = false;
async function saveReminder() {
  if (saveReminderInflight) return;
  if (!state.currentId) return;
  if (state.offlineMode) { toast('Reminders require a connection', true); return; }
  const d = ($('rem-date') as HTMLInputElement).value;
  const t = ($('rem-time') as HTMLInputElement).value;
  if (!d || !t) { toast('Pick a date and time', true); return; }
  const dt = new Date(`${d}T${t}`);
  if (isNaN(dt.getTime())) { toast('Invalid date', true); return; }
  if (dt.getTime() < Date.now()) { toast('Time is in the past', true); return; }
  saveReminderInflight = true;
  try {
    const r = await api<Reminder>('POST', '/api/reminders', {
      note_id: state.currentId,
      trigger_time: dt.toISOString(),
    });
    state.reminders.push(r);
    renderReminders();
    closeReminderModal();
    toast('Reminder added');
    // Now that the user has explicitly opted in to a reminder, ask for
    // notification permission. This is a real user gesture so browsers
    // honour the prompt instead of the silent-deny dance.
    const granted = await ensureNotificationPermission();
    if (!granted && 'Notification' in window && Notification.permission === 'denied') {
      toast('Browser notifications are blocked — reminders will only show as in-app toasts.', true);
    }
  } catch (e) { toast((e as Error).message, true); }
  finally { saveReminderInflight = false; }
}

// ----------------- Confirm dialog -----------------
let confirmResolver: ((v: boolean) => void) | null = null;
function confirmDialog(opts: {
  title: string;
  message: string;
  confirmLabel?: string;
  variant?: 'danger' | 'primary';
}): Promise<boolean> {
  $('confirm-title').textContent = opts.title;
  $('confirm-message').textContent = opts.message;
  const ok = $('confirm-ok') as HTMLButtonElement;
  ok.textContent = opts.confirmLabel || 'Confirm';
  // Default to the destructive (danger) styling, matching today's call sites.
  ok.classList.toggle('danger', (opts.variant ?? 'danger') === 'danger');
  ok.classList.toggle('primary', opts.variant === 'primary');
  $('confirm-modal').classList.remove('hidden');
  ok.focus();
  return new Promise<boolean>((resolve) => { confirmResolver = resolve; });
}
function resolveConfirm(v: boolean) {
  $('confirm-modal').classList.add('hidden');
  if (confirmResolver) { confirmResolver(v); confirmResolver = null; }
}

// ----------------- Note-to-note links -----------------
async function copyNoteLink() {
  if (!state.currentId) return;
  const link = `swatnotes:${state.currentId}`;
  try {
    await navigator.clipboard.writeText(link);
    toast(`Link copied — use as href="${link}" in any HTML note`);
  } catch {
    toast(`Note link: ${link}`);
  }
}

// ----------------- Note type picker -----------------
let noteTypeResolver: ((t: string | null) => void) | null = null;
function pickNoteType(): Promise<string | null> {
  $('type-picker-modal').classList.remove('hidden');
  ($('type-opt-rich') as HTMLButtonElement).focus();
  return new Promise<string | null>((resolve) => { noteTypeResolver = resolve; });
}
function resolveNoteType(t: string | null) {
  $('type-picker-modal').classList.add('hidden');
  if (noteTypeResolver) { noteTypeResolver(t); noteTypeResolver = null; }
}

// ----------------- Theme -----------------
function applyTheme(theme: 'dark' | 'light') {
  document.documentElement.setAttribute('data-theme', theme);
  const btn = document.getElementById('theme-toggle');
  if (btn) {
    btn.innerHTML = theme === 'dark'
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 15A9 9 0 019 3a9 9 0 1011 12z"/></svg>';
    btn.setAttribute('aria-label', theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme');
  }
}
function loadTheme(): 'dark' | 'light' {
  const saved = localStorage.getItem('swatnotes-theme');
  if (saved === 'dark' || saved === 'light') return saved;
  return 'dark';
}
function toggleTheme() {
  const current = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  const next = current === 'dark' ? 'light' : 'dark';
  localStorage.setItem('swatnotes-theme', next);
  applyTheme(next);
}

// ----------------- Notebooks -----------------
const NB_KEY = 'swatnotes-notebook-id';

function renderNotebookSelector() {
  const list = $('nb-list');
  list.innerHTML = '';
  ($('nb-name') as HTMLElement).textContent = state.currentNotebook?.name || '';

  for (const nb of state.notebooks) {
    const row = document.createElement('div');
    row.className = 'nb-item' + (nb.id === state.currentNotebook?.id ? ' active' : '');

    const nameBtn = document.createElement('button');
    nameBtn.className = 'nb-item-name';
    nameBtn.textContent = nb.name;
    nameBtn.addEventListener('click', () => { closeNbDropdown(); switchNotebook(nb); });

    const delBtn = document.createElement('button');
    delBtn.className = 'nb-item-del icon-btn';
    delBtn.title = 'Delete notebook';
    delBtn.textContent = '🗑';
    delBtn.addEventListener('click', (e) => { e.stopPropagation(); deleteNotebook(nb); });

    row.appendChild(nameBtn);
    row.appendChild(delBtn);
    list.appendChild(row);
  }

  // Restore the "＋ New notebook" button (may have been replaced by the input row)
  const existing = $('nb-new-btn');
  if (!existing) {
    const btn = document.createElement('button');
    btn.id = 'nb-new-btn';
    btn.className = 'nb-new-btn';
    btn.textContent = '＋ New notebook';
    $('nb-dropdown').appendChild(btn);
    wireNbNewBtn(btn);
  }
}

function wireNbNewBtn(btn: HTMLElement) {
  btn.addEventListener('click', () => {
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Notebook name';
    input.className = 'nb-new-input';

    const ok = document.createElement('button');
    ok.textContent = '✓';
    ok.className = 'icon-btn';

    const cancel = document.createElement('button');
    cancel.textContent = '✕';
    cancel.className = 'icon-btn';

    const row = document.createElement('div');
    row.className = 'nb-new-row';
    row.appendChild(input);
    row.appendChild(ok);
    row.appendChild(cancel);
    btn.replaceWith(row);
    input.focus();

    const submit = async () => {
      const name = input.value.trim();
      if (!name) { renderNotebookSelector(); return; }
      try {
        const nb = await api<Notebook>('POST', '/api/notebooks', { name });
        state.notebooks.push(nb);
        renderNotebookSelector();
        toast(`Notebook "${nb.name}" created`);
      } catch (e) {
        toast((e as Error).message, true);
        renderNotebookSelector();
      }
    };
    ok.addEventListener('click', submit);
    cancel.addEventListener('click', () => renderNotebookSelector());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
      if (e.key === 'Escape') renderNotebookSelector();
    });
  });
}

function closeNbDropdown() { $('nb-dropdown').classList.add('hidden'); }
function toggleNbDropdown() { $('nb-dropdown').classList.toggle('hidden'); }

async function switchNotebook(nb: Notebook) {
  if (nb.id === state.currentNotebook?.id) return;
  if (isInkModalOpen() && !await dismissInkModal()) return;
  if (!await finishDrawMode()) return;
  // Flush pending edits before switching so changes are not silently lost.
  if (state.dirty || pendingSave) {
    try { await flushSave(); } catch { return; /* Keep the editor and unsaved content. */ }
  }
  if (state.saveTimer !== null) { window.clearTimeout(state.saveTimer); state.saveTimer = null; }
  state.dirty = false;
  state.currentId = null;
  state.attachments = [];
  state.currentFilter = 'all';
  state.trashNotes = [];
  state.trashCount = 0;
  state.trashLoaded = false;
  if (isInkModalOpen()) dismissInkModal();
  $('editor-overlay').classList.add('hidden');

  state.currentNotebook = nb;
  localStorage.setItem(NB_KEY, nb.id);
  ($('nb-name') as HTMLElement).textContent = nb.name;

  if (state.offlineMode) {
    state.notes = loadCachedNotes<Note>(nb.id) ?? [];
    state.collections = loadCachedCollections<Collection>(nb.id) ?? [];
    renderSidebar();
    renderGrid();
    return;
  }

  try {
    await loadAll();
    await loadReminders();
  } catch (e) {
    if (isNetworkError(e)) {
      setOfflineMode(true);
      state.notes = loadCachedNotes<Note>(nb.id) ?? [];
      state.collections = loadCachedCollections<Collection>(nb.id) ?? [];
      renderSidebar();
      renderGrid();
    } else {
      toast(`Failed to load notebook: ${(e as Error).message}`, true);
    }
  }
}

async function deleteNotebook(nb: Notebook) {
  if (state.dirty || pendingSave) { try { await flushSave(); } catch { return; } }
  if (hasPendingOps()) { toast('Sync your offline changes before deleting a notebook.', true); return; }
  const ok = await confirmDialog({
    title: `Delete "${nb.name}"?`,
    message: 'All notes, categories, attachments and reminders in this notebook will be permanently deleted. This cannot be undone.',
    confirmLabel: 'Delete notebook',
  });
  if (!ok) return;
  try {
    await api('DELETE', `/api/notebooks/${nb.id}`);
    const deletedId = nb.id;
    clearNotebookCache(deletedId);
    state.notebooks = state.notebooks.filter(n => n.id !== deletedId);
    persistNotebooks(state.notebooks);
    if (state.currentNotebook?.id === deletedId) {
      const next = state.notebooks[0];
      if (next) {
        await switchNotebook(next);
      } else {
        state.currentNotebook = null;
        state.notes = [];
        state.collections = [];
        state.currentId = null;
        state.attachments = [];
        $('editor-overlay').classList.add('hidden');
        renderSidebar();
        renderGrid();
      }
    }
    renderNotebookSelector();
    toast(`Notebook "${nb.name}" deleted`);
  } catch (e) {
    toast((e as Error).message, true);
  }
}

// ----------------- SSE -----------------
async function fireReminderNotification(data: ReminderEvent) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const title = 'SwatNotes reminder';
  const body = data.note_title || 'Reminder';
  const tag = `reminder-${data.reminder_id}`;
  // Prefer the service worker so the notification is persistent (requireInteraction)
  // and survives the page being minimised/backgrounded.
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (reg?.active) {
      reg.active.postMessage({ type: 'show-reminder', title, body, tag });
      return;
    }
  } catch { /* fall through */ }
  new Notification(title, { body, tag, requireInteraction: true });
}

function connectEvents() {
  if (state.evtSource) state.evtSource.close();
  const es = new EventSource('/api/events');
  state.evtSource = es;
  const handler = (ev: MessageEvent) => {
    try {
      const data = JSON.parse(ev.data) as ReminderEvent;
      const title = data.note_title || 'Reminder';
      toast(`⏰ ${title}`);
      fireReminderNotification(data);
      loadReminders();
    } catch { /* ignore */ }
  };
  es.addEventListener('reminder', handler as EventListener);
  es.onmessage = handler;
  es.onerror = () => { /* auto-reconnect */ };
}

// ----------------- Auth (WebAuthn) -----------------
function b64uToBytes(s: string): ArrayBuffer {
  s = s.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}
function bytesToB64u(buf: ArrayBuffer): string {
  const b = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i] ?? 0);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
interface RequestOptionsJson {
  publicKey: {
    challenge: string;
    timeout?: number;
    rpId?: string;
    allowCredentials?: { type: string; id: string; transports?: string[] }[];
    userVerification?: UserVerificationRequirement;
    extensions?: AuthenticationExtensionsClientInputs;
  };
}
async function passkeyLogin(): Promise<
  | { ok: true }
  | { ok: false; error: string; retryAfter?: number; noCredentials?: boolean }
> {
  try {
    const startRes = await fetch('/auth/login/start', { method: 'POST', credentials: 'same-origin' });
    if (startRes.status === 409) return { ok: false, error: 'No passkeys registered.', noCredentials: true };
    if (!startRes.ok) return { ok: false, error: `login/start: ${startRes.status}` };
    const opts = (await startRes.json()) as RequestOptionsJson;
    const publicKey: PublicKeyCredentialRequestOptions = {
      ...opts.publicKey,
      challenge: b64uToBytes(opts.publicKey.challenge),
      allowCredentials: (opts.publicKey.allowCredentials || []).map(c => {
        const desc: PublicKeyCredentialDescriptor = {
          type: c.type as PublicKeyCredentialType,
          id: b64uToBytes(c.id),
        };
        if (c.transports) desc.transports = c.transports as AuthenticatorTransport[];
        return desc;
      }),
    };
    const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
    if (!cred) return { ok: false, error: 'No credential returned' };
    const resp = cred.response as AuthenticatorAssertionResponse;
    const body = {
      id: cred.id, rawId: bytesToB64u(cred.rawId), type: cred.type,
      response: {
        clientDataJSON: bytesToB64u(resp.clientDataJSON),
        authenticatorData: bytesToB64u(resp.authenticatorData),
        signature: bytesToB64u(resp.signature),
        userHandle: resp.userHandle ? bytesToB64u(resp.userHandle) : null,
      },
      extensions: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    };
    const finishRes = await fetch('/auth/login/finish', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (finishRes.ok) return { ok: true };
    let retryAfter: number | undefined;
    let error = `login/finish: ${finishRes.status}`;
    try {
      const j = (await finishRes.json()) as { error?: { code: string; message: string }; retry_after_secs?: number };
      const extracted = extractApiError(j);
      if (extracted) error = extracted;
      if (typeof j.retry_after_secs === 'number') retryAfter = j.retry_after_secs;
    } catch { /* ignore */ }
    if (finishRes.status === 429 && retryAfter === undefined) {
      const h = finishRes.headers.get('retry-after');
      if (h) retryAfter = parseInt(h, 10);
    }
    return retryAfter !== undefined
      ? { ok: false, error, retryAfter }
      : { ok: false, error };
  } catch (e) {
    return { ok: false, error: (e as Error).message || 'passkey error' };
  }
}
function resetClientState() {
  // Tear down everything that points at the previous user's data so a
  // re-login (or another user signing in on a shared device) starts clean.
  if (state.saveTimer !== null) {
    window.clearTimeout(state.saveTimer);
    state.saveTimer = null;
  }
  // Clear offline queues and caches before wiping state.notebooks so we still
  // have the notebook IDs needed to remove per-notebook cache entries.
  clearOfflineData(state.notebooks.map(nb => nb.id));
  setOfflineMode(false);
  state.notes = [];
  state.collections = [];
  state.reminders = [];
  state.attachments = [];
  state.currentFilter = 'all';
  state.currentId = null;
  state.dirty = false;
  state.editingCatId = null;
  state.titleEditedThisSession = false;
  state.noteHistory = [];
  if (state.iframeBlobUrl) {
    URL.revokeObjectURL(state.iframeBlobUrl);
    state.iframeBlobUrl = null;
  }
  state.noteType = 'rich';
  state.catlockConfigured = false;
  state.catlockSessionUnlocked = false;
  $('catlock-lock-btn').classList.add('hidden');
  // Hide every overlay/modal that might be open.
  closeInkModal();
  deactivateDrawMode();
  $('editor-overlay').classList.add('hidden');
  $('cat-modal').classList.add('hidden');
  $('reminder-modal').classList.add('hidden');
  $('confirm-modal').classList.add('hidden');
  closeNbDropdown();
  setHighlighterTool('off');
  updateBackButton();
}

async function logout() {
  if (quickNotes?.dirty && !await quickNotes.save()) return;
  if (state.currentId) {
    await closeEditor();
    if (state.currentId) return;
  }
  if (hasPendingOps()) { toast('Reconnect and sync your offline changes before signing out.', true); return; }
  try { await api('POST', '/auth/logout'); } catch { /* ignore */ }
  if (state.evtSource) state.evtSource.close();
  state.evtSource = null;
  if (refreshTimer !== null) {
    window.clearInterval(refreshTimer);
    refreshTimer = null;
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  resetClientState();
  showLogin();
}
async function uploadSvgDrawing(svgText: string, filename: string): Promise<Attachment> {
  if (!state.currentId) throw new Error('No note open');
  const file = new File([svgText], filename, { type: 'image/svg+xml' });
  const att = await uploadAttachment(file);
  if (!att) throw new Error('Upload failed');
  return att;
}

async function insertNewDrawing() {
  if (!state.currentId) return;
  if (!await finishDrawMode()) return;
  openInkModal({
    onSave: async (svg) => {
      const fname = `drawing-${Date.now()}.svg`;
      const att = await uploadSvgDrawing(svg, fname);
      const q = state.quill;
      if (!q) return;
      const sel = q.getSelection(true);
      const idx = sel ? sel.index : q.getLength();
      q.insertEmbed(idx, 'ink-drawing', {
        attachmentId: att.id,
        blobHash: att.blob_hash,
        filename: att.filename,
      }, 'user');
      q.setSelection(idx + 1, 0, 'silent');
      scheduleSave();
    },
  });
}

async function editExistingDrawing(node: HTMLElement, blobHash: string) {
  if (!state.currentId) return;
  const noteId = state.currentId;
  let existingSvg: string;
  try {
    const res = await fetch(`/api/attachments/blob/${encodeURIComponent(blobHash)}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    existingSvg = await res.text();
  } catch (e) {
    throw new Error(`Could not load drawing: ${(e as Error).message}. Please retry; the original is unchanged.`);
  }
  if (state.currentId !== noteId || !node.isConnected || isInkModalOpen()) return;

  openInkModal({
    existingSvg,
    onSave: async (svg) => {
      const fname = `drawing-${Date.now()}.svg`;
      const att = await uploadSvgDrawing(svg, fname);
      node.setAttribute('data-attachment-id', att.id);
      node.setAttribute('data-blob-hash', att.blob_hash);
      node.setAttribute('data-filename', att.filename);
      const img = node.querySelector('img.ink-drawing-img') as HTMLImageElement | null;
      if (img) img.src = `/api/attachments/blob/${encodeURIComponent(att.blob_hash)}`;
      // Keep earlier versions attached: a failed note save, another open
      // editor, or Undo can still reference them. They remain removable through
      // the normal attachment controls.
      scheduleSave();
    },
  });
}

async function bootstrap() {
  let status: { authenticated: boolean; has_credentials?: boolean };
  try {
    status = await api<{ authenticated: boolean; has_credentials?: boolean }>('GET', '/auth/status');
  } catch (e) {
    // If the network is down and we have cached data, skip the auth check and
    // let startApp() enter offline mode. Without connectivity we can't verify
    // the session, but cached notebooks are evidence the user was authenticated.
    if (isNetworkError(e) && loadCachedNotebooks<Notebook>()?.length) {
      await startApp();
      return;
    }
    showLogin();
    return;
  }
  if (status.has_credentials === false) {
    $('no-credentials').classList.remove('hidden');
    ($('passkey-signin') as HTMLButtonElement).disabled = true;
  }
  if (!status.authenticated) { showLogin(); return; }
  await startApp();
}
async function startApp() {
  showApp();

  if (!navigator.onLine) {
    if (!loadFromCache()) {
      showStartupError("You're offline and no cached data is available.", startApp);
      return;
    }
    setOfflineMode(true);
    connectEvents();
    startPeriodicRefresh();
    return;
  }

  // Check vault status before loading any notes. If vault is set up but locked,
  // show the unlock modal and wait for the user to unlock before proceeding.
  try {
    const vs = await api<{ setup: boolean; unlocked: boolean }>('GET', '/auth/vault/status');
    if (vs.setup && !vs.unlocked) {
      await waitForVaultUnlock();
    }
    if (vs.setup && hasPendingOps()) {
      // Encryption may have been enabled on another device while this browser
      // was offline. Save its queued work before removing plaintext storage.
      await checkCatlockStatus();
      if (state.catlockConfigured && !state.catlockSessionUnlocked && !await waitForCatlockUnlock()) {
        throw new Error('Unlock categories to sync your offline changes.');
      }
      await flushOfflineQueue(api);
    }
    setVaultCachePolicy(vs.setup);
    if (!vs.setup && !localStorage.getItem('vaultSetupDeclined')) {
      // Offer to set up the vault (non-blocking — user can skip for now).
      // If the user previously clicked "Skip", we respect that choice permanently
      // until they enable the vault via Settings.
      await offerVaultSetup();
    }
    // If vault is active, disable offline mode.
    if (vs.setup) {
      setOfflineMode(false);
      document.body.classList.remove('offline-mode');
      clearAllOfflineData();
      $('vault-lock-btn').classList.remove('hidden');
    }
  } catch (e) {
    if ((e as Error).message === 'unauthenticated') return;
    showStartupError(`Could not finish encryption checks: ${(e as Error).message}. Reconnect and retry.`, startApp);
    return;
  }

  await quickNotes.load(sanitizeQuickNotes);
  await checkCatlockStatus();

  // Notification permission is requested on first reminder save (a real
  // user gesture). Asking on app load triggers Chrome's "abusive
  // permission requests" mitigation and is silently dropped.
  try {
    state.notebooks = await api<Notebook[]>('GET', '/api/notebooks');
    persistNotebooks(state.notebooks);
    const savedId = localStorage.getItem(NB_KEY);
    state.currentNotebook = state.notebooks.find(nb => nb.id === savedId) ?? state.notebooks[0] ?? null;
    if (!state.currentNotebook) {
      showStartupError('No notebooks found. Please create one.', startApp);
      return;
    }
    renderNotebookSelector();
    await loadAll();
    await loadReminders();
  } catch (e) {
    if (isNetworkError(e)) {
      if (!loadFromCache()) {
        showStartupError("You're offline and no cached data is available.", startApp);
        return;
      }
      setOfflineMode(true);
      connectEvents();
      startPeriodicRefresh();
      return;
    }
    showStartupError((e as Error).message || 'Failed to load notes', startApp);
    return;
  }
  connectEvents();
  startPeriodicRefresh();
  if (hasPendingOps()) syncOfflineChanges().catch((e) => logWarn('startup sync failed', e));
}

function showStartupError(message: string, retry: () => Promise<void> | void) {
  const main = $('main');
  // Render an inline retry banner so the user knows what's wrong instead of
  // staring at an empty grid.
  main.innerHTML = '';
  const banner = document.createElement('div');
  banner.className = 'startup-error';
  const msg = document.createElement('p');
  msg.textContent = `Couldn't load your notes: ${message}`;
  const btn = document.createElement('button');
  btn.className = 'primary';
  btn.textContent = 'Retry';
  btn.addEventListener('click', () => {
    main.innerHTML = '';
    main.appendChild(buildMainHeadAndGrid());
    Promise.resolve(retry()).catch((e) => {
      showStartupError((e as Error).message || 'Failed to load notes', retry);
    });
  });
  banner.appendChild(msg);
  banner.appendChild(btn);
  main.appendChild(banner);
}

function buildMainHeadAndGrid(): DocumentFragment {
  const tpl = `
    <div id="main-head">
      <h2 id="current-cat-name">All Notes</h2>
      <button id="new-note-btn" class="primary">＋ New note</button>
    </div>
    <div id="notes-grid"></div>
    <div id="empty-grid" class="empty hidden">
      <p>No notes here yet.</p>
      <button id="empty-new-btn" class="primary">＋ Create your first note</button>
    </div>`;
  const t = document.createElement('template');
  t.innerHTML = tpl.trim();
  // Re-wire the two buttons we just recreated.
  const frag = t.content;
  frag.querySelector('#new-note-btn')!.addEventListener('click', () =>
    createNote().catch((e) => toast((e as Error).message, true)));
  frag.querySelector('#empty-new-btn')!.addEventListener('click', () =>
    createNote().catch((e) => toast((e as Error).message, true)));
  return frag;
}

async function ensureNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch { return false; }
}

// Poll for note/collection changes from other clients (e.g. desktop app or
// another browser) so the list stays current without a manual reload.
let refreshTimer: number | null = null;
let visibilityHandler: (() => void) | null = null;
async function refreshFromServer() {
  if (state.offlineMode || document.hidden) return;
  if (hasPendingOps()) {
    syncOfflineChanges().catch((e) => logWarn('periodic sync failed', e));
    return;
  }
  // Skip if the editor is open so we don't blow away in-flight edits.
  if (state.currentId !== null) return;
  if (state.dirty) return;
  const nbId = state.currentNotebook?.id;
  if (!nbId) return;
  try {
    const [notes, cols] = await Promise.all([
      api<Note[]>('GET', `/api/notes?notebook_id=${encodeURIComponent(nbId)}`),
      api<Collection[]>('GET', `/api/collections?notebook_id=${encodeURIComponent(nbId)}`),
    ]);
    const changed =
      notes.length !== state.notes.length ||
      cols.length !== state.collections.length ||
      notes.some((n, i) => {
        const cur = state.notes[i];
        return !cur || cur.id !== n.id || cur.updated_at !== n.updated_at;
      });
    if (!changed) return;
    state.notes = notes;
    state.collections = cols;
    persistNotes(nbId, notes);
    persistCollections(nbId, cols);
    renderSidebar();
    renderGrid();
  } catch (e) {
    // Silent — auth failures already redirect to login via api().
    console.debug('refresh skipped', e);
  }
}
function startPeriodicRefresh() {
  if (refreshTimer !== null) return;
  refreshTimer = window.setInterval(refreshFromServer, 30000);
  if (!visibilityHandler) {
    visibilityHandler = () => { if (!document.hidden) refreshFromServer(); };
    document.addEventListener('visibilitychange', visibilityHandler);
  }
}

// ----------------- Wire up -----------------
function wire() {
  installViewport();
  $('passkey-signin').addEventListener('click', async () => {
    const err = $('login-error');
    err.classList.add('hidden');
    const btn = $('passkey-signin') as HTMLButtonElement;
    btn.disabled = true;
    try {
      const result = await passkeyLogin();
      if (result.ok) { await startApp(); return; }
      if (result.noCredentials) { $('no-credentials').classList.remove('hidden'); return; }
      if (result.retryAfter !== undefined) {
        const mins = Math.ceil(result.retryAfter / 60);
        err.textContent = `Too many attempts. Try again in ~${mins} minute${mins === 1 ? '' : 's'}.`;
      } else {
        err.textContent = result.error || 'Sign-in failed';
      }
      err.classList.remove('hidden');
    } finally { btn.disabled = false; }
  });

  document.querySelectorAll('#sidebar .cat-item[data-cat="all"], #sidebar .cat-item[data-cat="uncategorized"], #sidebar .cat-item[data-cat="trash"]')
    .forEach(el => el.addEventListener('click', () => setFilter((el as HTMLElement).dataset.cat!)));

  $('empty-trash-btn').addEventListener('click', async () => {
    if (!state.trashNotes.length) return;
    if (!state.currentNotebook) return;
    const nbId = state.currentNotebook.id;
    const nbName = state.currentNotebook.name;
    const n = state.trashNotes.length;
    const ok = await confirmDialog({
      title: 'Empty Trash?',
      message: `Permanently delete all ${n} note${n === 1 ? '' : 's'} in “${nbName}” trash? This cannot be undone.`,
      confirmLabel: 'Empty Trash',
    });
    if (!ok) return;
    const btn = $('empty-trash-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Emptying…';
    try {
      await api('POST', `/api/notes/trash/prune?notebook_id=${encodeURIComponent(nbId)}`);
      state.trashNotes = [];
      state.trashCount = 0;
      renderSidebar();
      renderGrid();
      toast('Trash emptied');
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`, true);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Empty Trash';
    }
  });

  $('new-cat-btn').addEventListener('click', () => openCatModal());
  $('cat-cancel').addEventListener('click', closeCatModal);
  $('cat-save').addEventListener('click', saveCat);
  $('cat-delete').addEventListener('click', deleteCat);

  $('logout-btn').addEventListener('click', logout);
  $('new-note-btn').addEventListener('click', () => createNote().catch(e => toast((e as Error).message, true)));
  $('empty-new-btn').addEventListener('click', () => createNote().catch(e => toast((e as Error).message, true)));
  $('search').addEventListener('input', (e) => {
    if ((e as InputEvent).isComposing) return;
    if (state.globalSearch) triggerGlobalSearch();
    else renderGrid();
  });
  $('global-search-btn').addEventListener('click', () => setGlobalSearch(!state.globalSearch));

  // Apple Pencil: Draw / Type mode toggles
  function setPencilMode(mode: 'draw' | 'type') {
    const drawBtn = document.getElementById('pencil-draw-btn');
    const typeBtn = document.getElementById('pencil-type-btn');
    if (drawBtn) drawBtn.classList.toggle('pencil-mode-active', mode === 'draw');
    if (typeBtn) typeBtn.classList.toggle('pencil-mode-active', mode === 'type');
    drawBtn?.setAttribute('aria-pressed', String(mode === 'draw'));
    typeBtn?.setAttribute('aria-pressed', String(mode === 'type'));
    // Disable highlighter buttons while in draw mode (overlay covers the editor).
    const hlBar = document.getElementById('highlighter-bar');
    if (hlBar) hlBar.classList.toggle('pencil-hl-disabled', mode === 'draw');
    setHighlighterTool('off');
  }

  $('pencil-draw-btn').addEventListener('click', () => {
    if (!state.currentId) { toast('Open a note first', true); return; }
    if (state.offlineMode) { toast('Drawings require a connection', true); return; }
    setPencilMode('draw');
    activateDrawMode();
  });
  $('pencil-type-btn').addEventListener('click', async () => {
    if (await finishDrawMode()) setPencilMode('type');
  });

  // Apple Pencil: highlighter toolbar
  document.querySelectorAll('[data-hl-tool]').forEach((el) => {
    el.addEventListener('click', () => {
      const t = (el as HTMLElement).dataset.hlTool as HighlighterTool;
      setHighlighterTool(getHighlighterTool() === t && t !== 'off' ? 'off' : t);
    });
  });
  setHighlighterTool('off');

  // Apple Pencil: drawing canvas (modal, for insert-drawing-btn toolbar button)
  wireInkModal(() => confirmDialog({ title: 'Discard drawing changes?', message: 'Your drawing changes have not been saved.', confirmLabel: 'Discard changes' }));
  $('insert-drawing-btn').addEventListener('click', () => {
    if (!state.currentId) { toast('Open a note first', true); return; }
    if (state.offlineMode) { toast('Drawings require a connection', true); return; }
    insertNewDrawing().catch((e) => toast((e as Error).message, true));
  });

  // Apple Pencil: inline drawing overlay (Draw mode)
  installDrawOverlay(
    ensureQuill(),
    (svg, fname) => uploadSvgDrawing(svg, fname),
    () => setPencilMode('type'),
    () => confirmDialog({ title: 'Discard this drawing?', message: 'The strokes in this drawing have not been saved.', confirmLabel: 'Discard drawing' }),
  );
  window.addEventListener('ink-edit-request', (e) => {
    const ev = e as CustomEvent<{ node: HTMLElement; attachmentId: string; blobHash: string }>;
    const d = ev.detail;
    if (!d) return;
    editExistingDrawing(d.node, d.blobHash).catch((err) => toast((err as Error).message, true));
  });

  $('tab-back').addEventListener('click', () => {
    const prevId = state.noteHistory.pop();
    updateBackButton();
    if (!prevId) return;
    openEditor(prevId, true)
      .then(() => togglePreview())
      .catch((err) => toast((err as Error).message, true));
  });
  $('tab-edit').addEventListener('click', () => { if (state.previewMode) togglePreview(); });
  $('tab-preview').addEventListener('click', () => { if (!state.previewMode) togglePreview(); });

  $('note-title').addEventListener('input', (e) => {
    if ((e as InputEvent).isComposing) return;
    state.titleEditedThisSession = true;
    scheduleSave();
  });
  $('note-cat').addEventListener('change', scheduleSave);
  $('move-note-btn').addEventListener('click', openMoveDialog);
  $('toggle-attachments').addEventListener('click', () => {
    const visible = $('editor-panel').classList.toggle('attachments-visible');
    $('toggle-attachments').setAttribute('aria-expanded', String(visible));
  });
  $('close-editor').addEventListener('click', () => {
    state.noteHistory = []; updateBackButton();
    closeEditor().catch((e) => logWarn('closeEditor failed', e));
  });
  $('delete-note').addEventListener('click', () => deleteCurrent().catch(e => toast(`Delete failed: ${(e as Error).message}`, true)));
  $('add-reminder').addEventListener('click', addReminder);
  $('copy-note-link').addEventListener('click', copyNoteLink);

  // Source textarea (markdown/html notes)
  $('note-source-textarea').addEventListener('input', scheduleSave);

  // Source file load button
  const sourceFileInput = $('source-file-input') as HTMLInputElement;
  $('source-file-btn').addEventListener('click', () => sourceFileInput.click());
  sourceFileInput.addEventListener('change', () => {
    const f = sourceFileInput.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      ($('note-source-textarea') as HTMLTextAreaElement).value = reader.result as string;
      scheduleSave();
    };
    reader.readAsText(f);
    sourceFileInput.value = '';
  });

  // URL load button
  async function loadFromUrl() {
    if (state.offlineMode) { toast('URL loading requires a connection', true); return; }
    const input = $('source-url-input') as HTMLInputElement;
    const url = input.value.trim();
    if (!url) return;
    const btn = $('source-url-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Loading…';
    try {
      const result = await api<{ content: string }>('POST', '/api/fetch-url', { url });
      const fetched = state.noteType === 'html' ? injectBase(result.content, url) : result.content;
      ($('note-source-textarea') as HTMLTextAreaElement).value = fetched;
      scheduleSave();
      input.value = '';
      toast('Content loaded from URL');
    } catch (e) {
      toast(`URL load failed: ${(e as Error).message}`, true);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Load URL';
    }
  }
  $('source-url-btn').addEventListener('click', loadFromUrl);
  $('source-url-input').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') loadFromUrl();
  });

  // Type picker modal
  $('type-opt-rich').addEventListener('click', () => resolveNoteType('rich'));
  $('type-opt-markdown').addEventListener('click', () => resolveNoteType('markdown'));
  $('type-opt-html').addEventListener('click', () => resolveNoteType('html'));
  $('type-picker-modal').addEventListener('click', (e) => {
    if (e.target === $('type-picker-modal')) resolveNoteType(null);
  });

  // Attach-file button in toolbar + side panel upload button
  const fileInput = $('attach-file-input') as HTMLInputElement;
  const runFilePicker = () => {
    if (state.offlineMode) { toast('Attachments require a connection', true); return; }
    fileInput.click();
  };
  $('attach-file-btn').addEventListener('click', runFilePicker);
  $('attachments-upload').addEventListener('click', runFilePicker);
  fileInput.addEventListener('change', async () => {
    const fs = fileInput.files;
    if (!fs) return;
    for (const f of Array.from(fs)) {
      const inline = f.type.startsWith('image/');
      await uploadAndInsert(f, inline);
    }
    fileInput.value = '';
  });

  let editorMouseDownOnOverlay = false;
  $('editor-overlay').addEventListener('mousedown', (e) => {
    editorMouseDownOnOverlay = e.target === $('editor-overlay');
  });
  $('editor-overlay').addEventListener('click', (e) => {
    if (e.target === $('editor-overlay') && editorMouseDownOnOverlay) {
      closeEditor().catch((err) => logWarn('closeEditor failed', err));
    }
    editorMouseDownOnOverlay = false;
  });

  // --- Editor resize handle (bottom-right drag) ---
  {
    let resizing = false;
    let resizeStartX = 0;
    let resizeStartY = 0;
    let resizeStartW = 0;
    let resizeStartH = 0;
    const panel = $('editor-panel') as HTMLElement;

    $('editor-resize-handle').addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      resizing = true;
      resizeStartX = e.clientX;
      resizeStartY = e.clientY;
      resizeStartW = panel.offsetWidth;
      resizeStartH = panel.offsetHeight;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'nwse-resize';
    });

    document.addEventListener('mousemove', (e) => {
      if (!resizing) return;
      const dx = e.clientX - resizeStartX;
      const dy = e.clientY - resizeStartY;
      let w = Math.min(resizeStartW + dx, window.innerWidth);
      let h = Math.min(resizeStartH + dy, window.innerHeight);
      w = Math.max(480, w);
      h = Math.max(320, h);
      panel.style.width = `${w}px`;
      panel.style.height = `${h}px`;
    });

    document.addEventListener('mouseup', () => {
      if (!resizing) return;
      resizing = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    });
  }

  $('rem-cancel').addEventListener('click', closeReminderModal);
  $('rem-save').addEventListener('click', saveReminder);
  $('rem-date').addEventListener('input', updateRemPreview);
  $('rem-time').addEventListener('input', updateRemPreview);
  document.querySelectorAll('#reminder-modal .chip').forEach(el => {
    el.addEventListener('click', () => applyRemPreset((el as HTMLElement).dataset.preset!));
  });
  $('reminder-modal').addEventListener('click', (e) => {
    if (e.target === $('reminder-modal')) closeReminderModal();
  });

  $('confirm-cancel').addEventListener('click', () => resolveConfirm(false));
  $('confirm-ok').addEventListener('click', () => resolveConfirm(true));
  $('confirm-modal').addEventListener('click', (e) => {
    if (e.target === $('confirm-modal')) resolveConfirm(false);
  });

  $('theme-toggle').addEventListener('click', toggleTheme);

  document.querySelectorAll<HTMLButtonElement>('#view-toggle .view-toggle-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.view as ViewMode | undefined;
      if (mode && (VIEW_MODES as readonly string[]).includes(mode)) setViewMode(mode);
    });
  });

  $('settings-btn').addEventListener('click', openSettings);
  $('settings-close').addEventListener('click', () => $('settings-modal').classList.add('hidden'));
  $('settings-modal').addEventListener('click', (e) => {
    if (e.target === $('settings-modal')) $('settings-modal').classList.add('hidden');
  });

  $('vault-lock-btn').addEventListener('click', async () => {
    try {
      if (!await quickNotes.save()) return;
      await api('POST', '/auth/vault/lock', {});
      await waitForVaultUnlock();
      if (state.currentNotebook) await loadAll();
    } catch (e) {
      if ((e as Error).message !== 'vault_locked') toast('Failed to lock vault', true);
    }
  });

  $('catlock-lock-btn').addEventListener('click', async () => {
    try {
      await api('POST', '/auth/catlock/lock', {});
      state.catlockSessionUnlocked = false;
      $('catlock-lock-btn').classList.add('hidden');
      if (state.currentNotebook) await loadAll();
      toast('Categories locked');
    } catch (e) {
      toast((e as Error).message || 'Failed to lock categories', true);
    }
  });

  $('nb-btn').addEventListener('click', toggleNbDropdown);
  wireNbNewBtn($('nb-new-btn'));
  document.addEventListener('click', (e) => {
    const sel = document.getElementById('nb-selector');
    if (sel && !sel.contains(e.target as Node)) closeNbDropdown();
  });

  const closeSidebar = () => {
    document.body.classList.remove('sidebar-open');
    $('sidebar-backdrop').classList.add('hidden');
  };
  $('sidebar-toggle').addEventListener('click', () => {
    const open = document.body.classList.toggle('sidebar-open');
    $('sidebar-backdrop').classList.toggle('hidden', !open);
  });
  $('sidebar-backdrop').addEventListener('click', closeSidebar);
  const phoneLayout = window.matchMedia('(max-width: 768px)');
  // Unfolding a foldable (or rotating) out of the phone layout shows the sidebar
  // inline; a drawer left open would leave its backdrop over the notes.
  phoneLayout.addEventListener('change', () => { if (!phoneLayout.matches) closeSidebar(); });
  document.getElementById('sidebar')!.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('.cat-item') && phoneLayout.matches) closeSidebar();
  });

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (!$('confirm-modal').classList.contains('hidden')) { resolveConfirm(false); return; }
      if (isInkModalOpen()) { dismissInkModal(); return; }
      if (!$('type-picker-modal').classList.contains('hidden')) { resolveNoteType(null); return; }
      if (!$('nb-dropdown').classList.contains('hidden')) { closeNbDropdown(); return; }
      if (!$('settings-modal').classList.contains('hidden')) { $('settings-modal').classList.add('hidden'); return; }
      if (!$('catlock-setup-modal').classList.contains('hidden')) { ($('catlock-setup-cancel') as HTMLButtonElement).click(); return; }
      if (!$('catlock-unlock-modal').classList.contains('hidden')) { ($('catlock-unlock-cancel') as HTMLButtonElement).click(); return; }
      if (!$('vault-setup-modal').classList.contains('hidden')) { ($('vault-setup-skip') as HTMLButtonElement).click(); return; }
      // Inner modals take priority over the editor — each gets its own return so
      // the editor doesn't also close when the user dismisses a dialog inside it.
      if (!$('reminder-modal').classList.contains('hidden')) { closeReminderModal(); return; }
      if (!$('cat-modal').classList.contains('hidden')) { closeCatModal(); return; }
      if (!$('editor-overlay').classList.contains('hidden')) {
        closeEditor().catch((err) => logWarn('closeEditor failed', err));
      }
    }
  });
  // Handle swatnotes: links clicked inside HTML preview iframes
  window.addEventListener('message', (e) => {
    // Sandboxed iframes without allow-same-origin have origin 'null'
    if (e.origin !== 'null' && e.origin !== window.location.origin) return;
    if (!e.data || typeof e.data !== 'object' || e.data.type !== 'open-note') return;
    const noteId = String(e.data.noteId || '');
    const anchor = String(e.data.anchor || '');
    if (!noteId) return;
    const note = state.notes.find(n => n.id === noteId && !n.deleted_at);
    if (!note) { toast('Linked note not found', true); return; }
    if (state.currentId) { state.noteHistory.push(state.currentId); updateBackButton(); }
    openEditor(noteId, true)
      .then(() => togglePreview(anchor))
      .catch((err) => toast((err as Error).message, true));
  });

  window.addEventListener('beforeunload', (e) => {
    if (state.dirty || pendingSave || hasUnsavedInk() || isInkModalOpen()) e.preventDefault();
  });

  wireQuickNotes();

  window.addEventListener('offline', () => {
    if (!state.offlineMode) setOfflineMode(true);
  });

  window.addEventListener('online', () => {
    if (state.dirty) flushSave().catch(() => { /* Save error is visible in the editor. */ });
    setOfflineMode(false);
    syncOfflineChanges().catch((e) => logWarn('syncOfflineChanges failed', e));
  });
}

// ----------------- Quick Notes -----------------

let quickNotes: ReturnType<typeof createQuickNotes>;
const sanitizeQuickNotes = (html: string) => DOMPurify.sanitize(html, { ADD_TAGS: ['img'], ADD_ATTR: ['src', 'alt'] });

function wireQuickNotes() {
  const area = $('quick-notes-area') as HTMLDivElement;
  const clearBtn = $('quick-notes-clear') as HTMLButtonElement;

  quickNotes = createQuickNotes(area, $('quick-notes-status'), $('quick-notes-retry') as HTMLButtonElement);
  const scheduleQnSave = () => quickNotes.changed();
  const refresh = () => {
    if (quickNotes.ready && !$('app').classList.contains('hidden') && document.visibilityState === 'visible' && document.activeElement !== area) {
      void quickNotes.load(sanitizeQuickNotes);
    }
  };
  $('quick-notes-retry').addEventListener('click', () => {
    if (quickNotes.dirty) void quickNotes.save();
    else void quickNotes.load(sanitizeQuickNotes);
  });
  window.addEventListener('focus', refresh);
  window.addEventListener('online', () => { if (quickNotes.dirty) void quickNotes.save(); else refresh(); });
  window.setInterval(refresh, 15000);
  $('quick-notes-open').addEventListener('click', () => {
    document.body.classList.add('sidebar-open');
    $('sidebar-backdrop').classList.remove('hidden');
    area.scrollIntoView({ block: 'center' });
    area.focus();
    if (!quickNotes.dirty) void quickNotes.load(sanitizeQuickNotes);
  });

  // Paste: handle image files, text falls through to browser default
  area.addEventListener('paste', (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(it => it.kind === 'file' && it.type.startsWith('image/'));
    if (imageItems.length === 0) return;
    e.preventDefault();
    for (const item of imageItems) {
      const file = item.getAsFile();
      if (file) uploadQnImage(file, area, scheduleQnSave);
    }
  });

  // Copy: when a single image is selected, write real pixel data to the
  // clipboard so apps that don't consume text/html get the actual image
  // rather than the alt-text fallback ("image.png").
  area.addEventListener('copy', (e: ClipboardEvent) => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
    const fragment = sel.getRangeAt(0).cloneContents();
    const imgs = fragment.querySelectorAll('img');
    if (imgs.length !== 1 || (fragment.textContent ?? '').trim() !== '') return;
    const src = (imgs[0] as HTMLImageElement).src;
    if (!src) return;
    e.preventDefault();
    fetch(src, { credentials: 'same-origin' })
      .then(r => r.blob())
      .then(async blob => {
        let png: Blob;
        if (blob.type === 'image/png') {
          png = blob;
        } else {
          const bitmap = await createImageBitmap(blob);
          const canvas = document.createElement('canvas');
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(bitmap, 0, 0);
          png = await new Promise<Blob>((res, rej) =>
            canvas.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/png'),
          );
        }
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      })
      .catch(() => { /* fall back to whatever the browser put in the clipboard */ });
  });

  // Drop: handle image files
  area.addEventListener('dragover', (e) => e.preventDefault());
  area.addEventListener('drop', (e: DragEvent) => {
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const images = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (images.length === 0) return;
    e.preventDefault();
    for (const file of images) uploadQnImage(file, area, scheduleQnSave);
  });

  clearBtn.addEventListener('click', async () => {
    if (!quickNotes.ready || !await confirmDialog({ title: 'Clear Quick Notes?', message: 'This clears the shared scratch pad on all devices.', confirmLabel: 'Clear' })) return;
    area.innerHTML = '';
    quickNotes.changed();
    if (await quickNotes.save()) toast('Quick notes cleared');
  });
}

async function uploadQnImage(file: File, area: HTMLDivElement, onDone: () => void) {
  const fd = new FormData();
  fd.append('file', file, file.name);
  try {
    const res = await fetch('/api/quicknotes/images', {
      method: 'POST', credentials: 'same-origin', body: fd,
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const img_data = await res.json() as { url: string; filename: string };
    const img = document.createElement('img');
    img.src = img_data.url;
    img.alt = img_data.filename;
    img.style.maxWidth = '100%';
    area.appendChild(img);
    area.appendChild(document.createElement('br'));
    onDone();
  } catch (e) {
    toast(`Quick Notes image upload failed: ${(e as Error).message}`, true);
  }
}

applyTheme(loadTheme());
state.viewMode = loadViewMode();
updateViewToggleUI();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => logWarn('sw register failed', e));
  });
}

wire();
bootstrap();
